// Chart mode (`/c/{slug}/chart`; workspace-modes note §3, spec 06 §Main screens 7; Chart UX pass
// 2026-10-21): the company as a structure with numbers on it, drawn the way Rippling, ChartHop
// and Pingboard draw it. A 56 px location header ("Org chart", the seat count, the seat-setup
// chip, Teams / Seats / New seat), the lens bar, a 40 px toolbar (Chart | Table, the period,
// "Find a seat or person", Teams, Fit, then the quieter tools: My line, Card details, View —
// lines, drag sets, expand / collapse — and the legend), then the canvas: a dotted field with one compact card per seat,
// collapsible branches, a visible drag handle, and hover actions (open, focus, add a report,
// lines, edit) with tooltips. An open seat is a dashed card. The tree opens fitted to the canvas
// (ChartHop / Miro): zoom controls in the corner (−, +, Fit, 100%), ⌘/Ctrl + wheel zooms at the
// pointer and dragging the empty canvas pans it.
// Managers re-parent a seat by dragging it onto another (or use "Lines" on the seat, the keyboard
// and touch way), and draw authority and delegation lines the same way; every change waits in a
// draft until "Review", which simulates it on the server and shows the diff before anything is
// written (`PUT org-chart/edges`, M1-04).
// Dimensions (CHART-DIMENSIONS, docs/design/2026-10-21-chart-dimensions.md): each Structure card
// carries the rows the viewer turned on — teams, responsibilities (the floor), KPI status, load
// and cost. The "Card details" menu toggles them with presets; the choice is remembered per
// viewer and company, a manager can save it as the company default, and Setup › Chart sets who
// sees each gated dimension — the server (`GET org-chart/dimensions`) leaves a hidden one out.
// Lenses (CHART-LENSES, docs/design/2026-10-21-chart-lenses.md; CHART-KB,
// docs/design/2026-10-21-chart-kb.md; KPI Beast's chart, in our model): the tabs under the
// header — Structure, Performance, Execution, Responsibilities, Tasks, Finance, Keystones,
// Overall, Completeness — re-draw EVERY card for that lens: thin dimension bars along the top
// (one per lens the seat carries, the chart's lens emphasised, an empty one faint), the title
// first in bold, the holder with a small avatar, the lens's score top right in its band, the
// seat-setup ring on every card in every lens, the lens's rows with actual items (measurables
// with actual / target, priorities with milestones, the missing setup items), and a footer
// "N measurements · View all" that opens the side panel. A card with nothing in the lens is
// dimmed. The "Teams" panel draws a dashed enclosure around a team's seats with its name as a
// chip. "Table" lists the seats × every lens's value. The choice lives in `?lens=` and is
// remembered per viewer and company. Fit never goes below the zoom at which card text is 8 px
// (wave 9): at the floor the canvas opens centred on the top and the person pans. With the seat
// panel open (W10) the canvas is narrower and Fit may go to 6 px text; past that it pans to the
// panel's seat.

import type { Lens, LensScore, LensThresholds } from "@companyos/domain-work/org";
import { lensThresholdsOf } from "@companyos/domain-work/org";
import { ApiError } from "@companyos/sdk";
import {
  BAND_LABELS,
  BANDS,
  Badge,
  BandBadge,
  Button,
  buttonVariants,
  CompletenessRing,
  cn,
  DimensionBars,
  DimensionRow,
  DimensionRows,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  FilterChip,
  InlineBar,
  LegendItem,
  LegendSection,
  LensBandDot,
  LensScore as LensScoreMark,
  LensSteps,
  LensTabs,
  LocationHeader,
  OrgTree,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchField,
  Segmented,
  Swatch,
  TeamEnclosure,
  Toolbar,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useIsMobile,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArmchairIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowUpRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  CrosshairIcon,
  EllipsisIcon,
  EyeOffIcon,
  GitBranchPlusIcon,
  GripVerticalIcon,
  InfoIcon,
  LayersIcon,
  LockIcon,
  MaximizeIcon,
  MinusIcon,
  NetworkIcon,
  PanelRightOpenIcon,
  PencilIcon,
  PlusIcon,
  RouteIcon,
  SlidersHorizontalIcon,
  Table2Icon,
  UserPlusIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import {
  type DragEvent,
  type ReactElement,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Api } from "../../api/client";
import type { Band } from "../../api/types";
import { isoWeekKey, keepFocus, useFocus } from "../../focus/focus";
import { shiftWeek, weekRangeLabel, weekStart } from "../../focus/weeks";
import { NoAccessPage } from "../../pages/status-pages";
import { BudgetPaceMark } from "../agents/budget-pace";
import { DePresenceCardProvider, DePresenceProvider, useDePresence } from "../chat/de-presence";
import { type CompanyAccess, isManager } from "../company";
import { budgetState } from "../finance/budget-vs-actual";
import { monthLabel, ratioText } from "../finance/finance-parts";
import { formatCardMetric, formatMoney, formatScore } from "../format";
import { HEALTH_TEXT, lineHealth } from "../kpi/health";
import { type TeamScore, teamScoresQuery } from "../kpi/team-queries";
import { TeamScoreChip, TeamScorecardCard } from "../kpi/team-scorecard";
import { digitalEmployeesQuery, keys, rolesQuery } from "../queries";
import { LoadError, Loading, usePeriod } from "../screen-parts";
import { SEAT_STATUS_LABELS } from "../structure/labels";
import { NewSeatDialog, SEAT_STATUS_TONE } from "../structure/seats-screen";
import { useFeature, useSession } from "../use-feature";
import { agentsBySeat, SeatAgentsChip, SeatAgentsList, useOpenAgents } from "./chart-agents";
import {
  DEFAULT_VISIBILITY,
  DIMENSION_HINTS,
  DIMENSION_LABELS,
  DIMENSIONS,
  type Dimension,
  effectiveDimensions,
  GATED,
  GATED_LABELS,
  hoursText,
  keepLine,
  LEVEL_LABELS,
  loadText,
  loadTone,
  myLine,
  needsDimensions,
  PRESET_DIMENSIONS,
  PRESET_LABELS,
  PRESETS,
  presetOf,
  readDimensionPrefs,
  type SeatDimensions,
  writeDimensionPrefs,
} from "./chart-dimensions";
import {
  branchOf,
  effectiveLens,
  LENS_HINTS,
  LENS_LABELS,
  LENS_UNITS,
  lensAudience,
  lensCarried,
  lensLocked,
  lensScoreOf,
  lensSteps,
  lensVisible,
  readLensPref,
  SCORED_LENSES,
  SCORING_LENSES,
  servedLenses,
  writeLensPref,
} from "./chart-lenses";
import {
  type ChartSeat,
  COMPOSITION_LABELS,
  type DraftOp,
  type DropPlace,
  dropOp,
  dropPlaceAt,
  dropRefusal,
  EDGE_KINDS,
  EDGE_LABELS,
  type EdgeKind,
  LINE_KINDS,
  LINE_VERBS,
  type LineKind,
  linesOf,
  type OrgChart,
  reorderIds,
  type SeatNode,
  scopeTeam,
  seatComposition,
  seatTree,
  stepIds,
  storedSiblings,
  supervisors,
  teamSeats,
  underOf,
  withOp,
  withOrder,
} from "./chart-model";
import {
  ChartBody,
  ChartFrame,
  chartCrumb,
  holderNames,
  SeatAvatar,
  seatNameText,
} from "./chart-parts";
import { chartKeys, orgChartDimensionsQuery, orgChartQuery } from "./chart-queries";
import {
  AccountableLine,
  accountableName,
  deHeld,
  type OwnerChip,
  OwnerChipBadge,
  ownerChip,
} from "./de-seat-owner";
import { EditSeatDrawer } from "./edit-seat-drawer";
import { COMPLETENESS_LABELS, TRACK_BAND, TRACK_LABELS } from "./keystones";
import { ReportsToDialog } from "./reports-to-dialog";
import { ReviewDialog } from "./review-dialog";
import { SeatLinesDialog } from "./seat-lines-dialog";
import { SeatPanel } from "./seat-panel";
import {
  deNameOf,
  homeTeamName,
  LINKED_DRAG_REFUSAL,
  LinkedCard,
  SharedBadge,
  sharedSeatIds,
} from "./shared-des";
import { TeamMemberDialog } from "./team-member-dialog";

export const OVERLAYS = ["structure", "kpi", "money"] as const;
export type Overlay = (typeof OVERLAYS)[number];
/** Chart (the tree) or Table (the seats × every lens's value). */
export type ChartView = "tree" | "table";
/** The period grain the ‹ › buttons step by: the week (Performance, Tasks), the month (Finance) or the quarter (Keystones). */
export type Grain = "week" | "month" | "quarter";

const VIEW_KEY = "companyos.chart.view";
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;
/** The Overall card's part rows, in the label column (CHART-LENSES). */
const PART_LABELS: Record<(typeof SCORED_LENSES)[number], string> = {
  performance: "Performance",
  execution: "Execution",
  responsibilities: "Coverage",
  tasks: "Tasks",
  finance: "Finance",
  keystones: "Priorities",
};
const GRAIN_LABELS: Record<Grain, string> = { week: "Week", month: "Month", quarter: "Quarter" };
/**
 * KPIS-MODE (founder, 2026-10-22): the lenses that read a period show the period control;
 * Structure, Responsibilities and Completeness read none (the chart as it is now).
 */
const PERIOD_LENSES: ReadonlySet<Lens> = new Set<Lens>([
  "performance",
  "execution",
  "tasks",
  "finance",
  "keystones",
  "overall",
]);
/** The lens that reads at each grain, for the period label. */
const GRAIN_OF_LENS: Partial<Record<Lens, Grain>> = {
  finance: "month",
  keystones: "quarter",
};

/**
 * A seat's children list: the 18 px stem from the card down to the crossbar (org-tree.css's
 * `ul ul::before`, which skips the first nested level), so every parent–child link is joined.
 */
export const SEAT_STEM =
  "relative before:absolute before:top-0 before:left-1/2 before:h-[18px] before:w-0 before:border-l before:border-strong";

/** Zoom bounds of the tree view and the step of the − / + buttons. */
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 2;
const ZOOM_STEP = 1.2;
const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
/** The smallest text a seat card draws at 100 % (its rows and chips, `--text-2xs`). */
const CARD_MIN_TEXT_PX = 11;
/**
 * The smallest card text "Fit" may leave on screen. Wave 9 review (KPI Beast fits a 15-seat org
 * on one screen): the founder prefers the whole org at a glance, so Fit shrinks to about 8 px
 * text (~73 %); zooming in restores full size.
 */
const FIT_MIN_TEXT_PX = 8;
/**
 * "Fit" never goes below this: the zoom at which the card's smallest text is still
 * `FIT_MIN_TEXT_PX`. A tree wider than that opens centred on its root and the person pans; −,
 * ⌘-wheel still zoom out by hand. Raising the card's base text lowers it.
 */
export const FIT_MIN = FIT_MIN_TEXT_PX / CARD_MIN_TEXT_PX;
/**
 * W10: with the seat panel open the canvas is the panel's width narrower (440 px by default), and
 * a tree that fits beside it at 6 px text is better whole than clipped at 8 px (Northwind's outer
 * cards were cut off). Past this floor Fit pans to the panel's seat.
 */
const FIT_MIN_PANEL_TEXT_PX = 6;
export const FIT_MIN_PANEL = FIT_MIN_PANEL_TEXT_PX / CARD_MIN_TEXT_PX;
/** The floor of "Fit": lower while the seat panel takes part of the canvas's width. */
export const fitFloor = (panelOpen: boolean): number => (panelOpen ? FIT_MIN_PANEL : FIT_MIN);
/** The card's width (wave 9: KPI Beast-sized, 170–190 px, dense rows; `w-[184px]` on the card). */
export const CARD_W = 184;
/**
 * CHART-TABLE-ORDER: the Table view's Seat cell — a 28 px gutter for the drag grip, then 24 px
 * per level (a fixed set: no style props outside packages/ui). The chevron (16 px) starts each
 * level, so level `i`'s guide line runs down the middle of its chevron: 28 + 24·i + 8 px.
 */
const DEPTH_PAD = [
  "pl-7",
  "pl-13",
  "pl-19",
  "pl-25",
  "pl-31",
  "pl-37",
  "pl-43",
  "pl-49",
  "pl-55",
] as const;
const GUIDE_LEFT = [
  "left-[36px]",
  "left-[60px]",
  "left-[84px]",
  "left-[108px]",
  "left-[132px]",
  "left-[156px]",
  "left-[180px]",
  "left-[204px]",
] as const;
const MAX_DEPTH = DEPTH_PAD.length - 1;

/** CHART-TABLE-ORDER: the collapsed branches, remembered per company in this browser. */
const collapsedKey = (co: string) => `companyos.chart.collapsed.${co}`;
function readCollapsed(co: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(collapsedKey(co));
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}
function writeCollapsed(co: string, ids: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(collapsedKey(co), JSON.stringify([...ids]));
  } catch {}
}

/**
 * The tree's size at 100%: the roots' bounding box (the tree's zoom is lifted for the
 * measurement and put back in the same frame, so nothing is painted), with the tree's gutters.
 * Without layout (tests) it is null.
 */
function treeNatural(tree: HTMLElement): { w: number; h: number } | null {
  const roots = tree.querySelectorAll<HTMLElement>(":scope > li");
  if (!roots.length) return null;
  const zoomed = tree.style.zoom;
  tree.style.zoom = "1";
  let left = Number.POSITIVE_INFINITY;
  let right = Number.NEGATIVE_INFINITY;
  let bottom = Number.NEGATIVE_INFINITY;
  for (const li of roots) {
    const r = li.getBoundingClientRect();
    left = Math.min(left, r.left);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  }
  const top = tree.getBoundingClientRect().top;
  tree.style.zoom = zoomed;
  // org-tree.css: 24 px either side, 4 px above (inside `top`) and 24 px below.
  return { w: right - left + 48, h: bottom - top + 24 };
}

/**
 * The largest zoom at which `natural` fits `box`, at most 100%, rounded down to a whole percent
 * so the tree never spills by a pixel; no floor (`fitOf` and `fitLayout` apply it).
 */
function rawFit(box: { w: number; h: number }, natural: { w: number; h: number } | null): number {
  if (!natural || !(natural.w > 0) || !(natural.h > 0) || !(box.w > 0) || !(box.h > 0)) return 1;
  return Math.floor(Math.min(box.w / natural.w, box.h / natural.h, 1) * 100) / 100;
}

/** The largest whole-percent zoom (100% at most, `floor` at least) at which `natural` fits `box`. */
export function fitOf(
  box: { w: number; h: number },
  natural: { w: number; h: number },
  floor: number = FIT_MIN,
): number {
  return clampZoom(Math.max(floor, rawFit(box, natural)));
}

/**
 * W9-FIXES: "Fit" measures the tree both ways — one row per level, and compact (leaf groups in
 * two columns, `OrgTree`'s `compact`) — and keeps the one that fits larger; a tie keeps the
 * rows. Northwind's nine seats were width-bound at 76% with half the canvas empty below; compact
 * they fit at about 100%. W10: the two are compared before the floor (both under it used to tie
 * at the floor and keep the wider rows), and with the seat panel open the floor is
 * `FIT_MIN_PANEL`: `box` is the canvas beside the panel (the panel takes its width).
 */
export function fitLayout(
  box: { w: number; h: number },
  rows: { w: number; h: number } | null,
  compact: { w: number; h: number } | null,
  panelOpen: boolean,
): { zoom: number; compact: boolean } {
  const pick = pickLayout(rawFit(box, rows), rawFit(box, compact));
  return { zoom: clampZoom(Math.max(fitFloor(panelOpen), pick.zoom)), compact: pick.compact };
}

/**
 * `fitLayout` on the live tree, against the canvas's full box (at a fitting zoom it has no
 * scrollbars). The compact attribute is toggled for the measurement and put back in the same
 * frame, like the zoom, so nothing is painted. Without layout (tests) it is 100%, rows.
 */
function fittingLayout(
  canvas: HTMLElement,
  tree: HTMLElement,
  panelOpen: boolean,
): { zoom: number; compact: boolean } {
  const box = { w: canvas.offsetWidth, h: canvas.offsetHeight };
  const was = tree.hasAttribute("data-compact");
  tree.removeAttribute("data-compact");
  const rows = treeNatural(tree);
  tree.setAttribute("data-compact", "");
  const compact = treeNatural(tree);
  if (!was) tree.removeAttribute("data-compact");
  return fitLayout(box, rows, compact, panelOpen);
}

/** Rows unless compact fits strictly larger. */
export function pickLayout(rows: number, compact: number): { zoom: number; compact: boolean } {
  return compact > rows + 0.001 ? { zoom: compact, compact: true } : { zoom: rows, compact: false };
}

/**
 * At the fit floor the tree overflows: open it with the root's card in the middle of the canvas
 * and the top row at the top (the person pans). Several roots: the one with the most seats.
 * W10: with the seat panel open (`seatId`), the panel's seat instead — centred across, and
 * scrolled down only as far as it takes to show its card — so the seat being read stays in view.
 */
function centreOnTop(canvas: HTMLElement, seatId: string | null = null): void {
  const max = Math.max(0, canvas.scrollWidth - canvas.clientWidth);
  // A tree that fits (100 % is the floor) is left where it is.
  if (max <= 1 && canvas.scrollHeight <= canvas.clientHeight + 1) return;
  let top: HTMLElement | null = null;
  const seat = seatId
    ? [...canvas.querySelectorAll<HTMLElement>("[data-seat]")].find(
        (li) => li.dataset.seat === seatId,
      )
    : undefined;
  if (seat) top = seat.querySelector<HTMLElement>(":scope > article");
  else {
    // The org's top: the root with the most seats under it (others are stray top-level seats).
    let most = -1;
    for (const li of canvas.querySelectorAll<HTMLElement>("[data-slot='seat-tree'] > li")) {
      // Every seat under it, staff seats (drawn as `div`s, CHART-STAFF) included.
      const n = li.querySelectorAll("[data-seat]").length;
      if (n > most) {
        most = n;
        top = li.querySelector<HTMLElement>(":scope > article");
      }
    }
  }
  let centre = (canvas.scrollWidth - canvas.clientWidth) / 2;
  let down = 0;
  if (top) {
    const box = canvas.getBoundingClientRect();
    const r = top.getBoundingClientRect();
    centre = r.left + r.width / 2 - box.left + canvas.scrollLeft - canvas.clientWidth / 2;
    if (seat) {
      const bottom = r.bottom - box.top + canvas.scrollTop + 24;
      down = Math.max(0, bottom - canvas.clientHeight);
    }
  }
  canvas.scrollLeft = Math.min(max, Math.max(0, centre));
  canvas.scrollTop = down;
}

/** A hover action with its tooltip; `render` is the button or link, `tip` its label. */
function Act({
  tip,
  render,
  children,
}: {
  tip: string;
  render: ReactElement;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger render={render}>{children}</TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}

function readView(): ChartView {
  try {
    const v = window.localStorage.getItem(VIEW_KEY);
    return v === "table" || v === "list" ? "table" : "tree";
  } catch {
    return "tree";
  }
}

/** The seats a search keeps: the matches (title or holder) and every seat above them. */
export function searchKeeps(
  seats: readonly ChartSeat[],
  sup: ReadonlyMap<string, string | null>,
  term: string,
): { shown: Set<string>; matched: Set<string> } | null {
  const q = term.trim().toLowerCase();
  if (!q) return null;
  const matched = new Set(
    seats
      .filter((s) => s.title.toLowerCase().includes(q) || holderNames(s).toLowerCase().includes(q))
      .map((s) => s.id),
  );
  const shown = new Set(matched);
  for (const id of matched) {
    let cur = sup.get(id) ?? null;
    while (cur && !shown.has(cur)) {
      shown.add(cur);
      cur = sup.get(cur) ?? null;
    }
  }
  return { shown, matched };
}

/** "2026-W42" → the week holding the 15th of the month `delta` months away (the ‹ › step at the month grain). */
function shiftMonthWeek(week: string, delta: number): string {
  const d = new Date(weekStart(week).getTime() + 3 * 86_400_000);
  const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + delta, 15));
  return isoWeekKey(new Date(m.getUTCFullYear(), m.getUTCMonth(), m.getUTCDate()));
}

export function ChartScreen({
  api,
  company,
  overlay = "structure",
  onOverlay,
  lens: lensParam,
  onLens,
  seat: seatParam,
  stage,
  onStaged,
}: {
  api: Api;
  company: CompanyAccess;
  overlay?: Overlay;
  onOverlay?: (overlay: Overlay) => void;
  /** CHART-LENSES: the lens in the URL (`?lens=`), when there is one. */
  lens?: Lens;
  onLens?: (lens: Lens) => void;
  /** PRIORITIES-HOME: a seat whose side panel opens with the chart (`?seat=`, from Priorities). */
  seat?: string;
  /** STRUCTURE-CHANGED: a move staged elsewhere (`?move=&under=`), added to the draft on load. */
  stage?: { seat: string; to: string | null } | undefined;
  /** The staged move is in the draft: the URL drops it (a reload does not stage it again). */
  onStaged?: () => void;
}) {
  const manager = isManager(company);
  const mobile = useIsMobile();
  const queryClient = useQueryClient();
  const { me } = useSession();
  const { focus, setFocus } = useFocus();
  const week = usePeriod();
  const chartQ = useQuery(orgChartQuery(api, company.id));
  const kpiFeature = useFeature("kpi");
  // CHAT-PRESENCE: the DE holders' rings on the seat cards (Chat's presence; none without Chat).
  const chatFeature = useFeature("chat");
  const rings = useDePresence(api, company.id, chatFeature.enabled);
  // …and their hover card (the Team list's): acting on it needs a membership and Chat writable.
  const meId = me.memberships.find((m) => m.company_id === company.id)?.membership_id ?? null;
  const presenceCard = useMemo(
    () => ({
      api,
      co: company.id,
      slug: company.slug,
      canAct: meId !== null && !chatFeature.readOnly,
    }),
    [api, company.id, company.slug, meId, chatFeature.readOnly],
  );
  const teamScores = useQuery({
    ...teamScoresQuery(api, company.id, week),
    enabled: kpiFeature.enabled,
  });
  // Roles are part of the floor every chart viewer reads (founder, 2026-10-21: members see
  // roles and responsibilities; `roles.list` is a viewer route).
  const roles = useInfiniteQuery(rolesQuery(api, company.id));

  // Dimensions: the viewer's choice (this browser) over the company default; the query runs
  // whenever a row beyond the teams is on. `?overlay=kpi|money` (old links) adds its dimension.
  const [prefs, setPrefs] = useState<Set<Dimension> | null>(() => readDimensionPrefs(company.id));
  const legacy = overlay === "kpi" || overlay === "money" ? overlay : undefined;
  // The lenses read the dimensions on every load (the floor's coverage, the scores), so the
  // query always runs; `needsDimensions` still decides whether a Structure card waits on it.
  const dimsQ = useQuery(orgChartDimensionsQuery(api, company.id, week));
  const dims = effectiveDimensions(
    prefs,
    dimsQ.data?.default_dimensions,
    dimsQ.data?.dimensions,
    legacy,
  );
  const served = dimsQ.data?.dimensions ?? DIMENSIONS;
  const visibility = dimsQ.data?.visibility ?? DEFAULT_VISIBILITY;
  const dimOf = new Map<string, SeatDimensions>(
    (dimsQ.data?.seats ?? []).map((d) => [d.seat_id, d]),
  );

  // Lenses (CHART-LENSES): the URL's, else the viewer's remembered one, else the company's
  // default; a lens the company does not serve or the viewer cannot open falls back to the floor.
  const [lensPref, setLensPref] = useState<Lens | null>(() => readLensPref(company.id));
  const lensesServed = servedLenses(served);
  const seatDims = dimsQ.data?.seats ?? [];
  const locked = (l: Lens) => lensLocked(l, seatDims);
  const lens = effectiveLens(
    lensParam,
    lensPref,
    dimsQ.data?.lenses.default_lens,
    lensesServed,
    locked,
  );
  const thresholds: LensThresholds = dimsQ.data?.lenses.thresholds ?? lensThresholdsOf(undefined);
  const pickLens = (next: Lens) => {
    setLensPref(next);
    writeLensPref(company.id, next);
    onLens?.(next);
  };
  const scoreOf = (s: ChartSeat): LensScore => lensScoreOf(lens, s, dimOf.get(s.id), thresholds);
  const chooseDims = (next: Set<Dimension> | null) => {
    setPrefs(next);
    writeDimensionPrefs(company.id, next);
    if (legacy) onOverlay?.("structure");
  };
  const toggleDim = (d: Dimension, on: boolean) => {
    const next = new Set(dims);
    if (on) next.add(d);
    else next.delete(d);
    chooseDims(next);
  };
  const saveDefault = useMutation({
    mutationFn: async () => {
      const detail = await api.client.companies.get({ params: { co: company.id } });
      return api.client.companies.update({
        params: { co: company.id },
        body: {
          settings: {
            ...detail.settings,
            chart: { ...detail.settings.chart, dimensions: DIMENSIONS.filter((d) => dims.has(d)) },
          },
        },
      });
    },
    onSuccess: (detail) => {
      queryClient.setQueryData(keys.company(company.id), detail);
      void queryClient.invalidateQueries({ queryKey: chartKeys.orgChart(company.id) });
      chooseDims(null);
      setNotice("Saved as the company's default dimensions.");
    },
  });
  const preset = presetOf(dims);
  const [line, setLine] = useState(false);
  const [panelSeat, setPanelSeat] = useState<string | null>(seatParam ?? null);
  const myMemberships = me.memberships
    .filter((m) => m.company_id === company.id)
    .map((m) => m.membership_id);
  // A focused DE (from Agents or Cadence) points at its seat; the DE list is only read then.
  const deFocus = focus.subject?.kind === "de" ? focus.subject.id : null;
  const des = useInfiniteQuery({
    ...digitalEmployeesQuery(api, company.id),
    enabled: deFocus !== null,
  });

  const [ops, setOps] = useState<DraftOp[]>([]);
  const [dragKind, setDragKind] = useState<EdgeKind>("reports_to");
  const [visibleLines, setVisibleLines] = useState<Set<LineKind>>(() => new Set(LINE_KINDS));
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [linesFor, setLinesFor] = useState<string | null>(null);
  /** MODALS: the seat whose Setup drawer is open over the chart. */
  const [editing, setEditing] = useState<string | null>(null);
  /** STRUCTURE-CHANGED: the seat whose "Reports to…" picker is open. */
  const [moving, setMoving] = useState<string | null>(null);
  /** MODALS: the team a seat is being added to (the chart scoped to an empty team). */
  const [joining, setJoining] = useState<{ id: string; name: string } | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [viewPref, setViewPref] = useState<ChartView>(readView);
  /** "Add a report": the supervisor of the new seat (null: top of the chart). */
  const [adding, setAdding] = useState<string | null | false>(false);
  const view: ChartView = mobile ? "table" : viewPref;
  // CHART-KB: the period grain the ‹ › buttons step by (the lens suggests it), the Teams panel
  // and the team it encloses.
  const [grainPref, setGrainPref] = useState<Grain | null>(null);
  const grain: Grain = grainPref ?? GRAIN_OF_LENS[lens] ?? "week";
  const [teamsOpen, setTeamsOpen] = useState(false);
  const [enclosed, setEnclosed] = useState<string | null>(null);
  const [enclosure, setEnclosure] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const stepPeriod = (delta: number) => {
    const next =
      grain === "week"
        ? shiftWeek(week, delta)
        : shiftMonthWeek(week, delta * (grain === "month" ? 1 : 3));
    setFocus({ period: next });
  };

  // Zoom and pan of the tree view. "Fit" (the default) keeps the whole tree in the canvas and
  // follows the canvas's size and the tree's shape; a zoom set by hand keeps the point under
  // the pointer (wheel) or the canvas's centre (buttons) where it was.
  const canvasRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const treeRef = useRef<HTMLUListElement>(null);
  const [zoom, setZoom] = useState(1);
  const [fit, setFit] = useState(true);
  // W9-FIXES: leaf groups in two columns when that lets "Fit" go larger (`fittingLayout`).
  const [compact, setCompact] = useState(false);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const [canvasSize, setCanvasSize] = useState("");
  const pendingScroll = useRef<{
    sl: number;
    st: number;
    ax: number;
    ay: number;
    from: number;
  } | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => readCollapsed(company.id));
  useEffect(() => writeCollapsed(company.id, collapsed), [company.id, collapsed]);
  /** CHART-TABLE-ORDER: the table row being dragged, and where it would land. */
  const [rowDrag, setRowDrag] = useState<string | null>(null);
  const [rowOver, setRowOver] = useState<{ id: string; place: DropPlace } | null>(null);
  // CHART-AGENTS: the seats whose agents are open, remembered per company in this browser.
  const [agentsOpen, toggleAgents] = useOpenAgents(company.id);
  const treeVersion = `${chartQ.dataUpdatedAt}:${dimsQ.dataUpdatedAt}`;
  // MODALS: what the person is looking at, without the data's version. A refetch (a quick add's
  // save, a change ping) re-lays the tree out but does not move the view the person panned to;
  // only a change of what is shown re-centres it.
  const viewKey = `${view}:${focus.scope}:${chartQ.isSuccess}:${dimsQ.isSuccess}:${canvasSize}:${[...collapsed].join(",")}:${[...agentsOpen].join(",")}:${search}:${[...dims].join(",")}:${lens}:${line}:${panelSeat ?? ""}`;
  const layoutKey = `${viewKey}:${treeVersion}`;
  const centredFor = useRef<string | null>(null);
  // The canvas exists only once the chart has loaded (the loading and error states have none), so
  // the observers attach then. The canvas's size and the tree's own size (cards growing as fonts,
  // roles or dimensions arrive) both refit; a zoom change re-sizes the tree, and the refit then
  // lands on the same zoom, so it settles.
  const fitRef = useRef(fit && view === "tree");
  fitRef.current = fit && view === "tree";
  // W10: the seat panel lowers Fit's floor, and at the floor Fit pans to its seat.
  const panelRef = useRef(panelSeat);
  panelRef.current = panelSeat;
  const hasCanvas = chartQ.isSuccess;
  useEffect(() => {
    const canvas = canvasRef.current;
    const tree = treeRef.current;
    if (!hasCanvas || !canvas || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      setCanvasSize(`${canvas.offsetWidth}x${canvas.offsetHeight}`);
      if (!fitRef.current || !tree) return;
      const next = fittingLayout(canvas, tree, panelRef.current !== null);
      setCompact(next.compact);
      if (Math.abs(next.zoom - zoomRef.current) > 0.001) setZoom(next.zoom);
    });
    ro.observe(canvas);
    if (tree) ro.observe(tree);
    return () => ro.disconnect();
  }, [hasCanvas]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refit whenever the tree is re-laid out (layoutKey) or "Fit" is picked
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const tree = treeRef.current;
    if (!fit || view !== "tree" || !canvas || !tree) {
      centredFor.current = null; // Fit picked again later centres again
      return;
    }
    const next = fittingLayout(canvas, tree, panelSeat !== null);
    setCompact(next.compact);
    if (Math.abs(next.zoom - zoomRef.current) > 0.001) setZoom(next.zoom);
    // At the floor the tree is wider than the canvas: open on the top row, centred (once per
    // view: a data refresh keeps where the person panned).
    else if (next.zoom <= fitFloor(panelSeat !== null) + 0.001 && centredFor.current !== viewKey)
      centreOnTop(canvas, panelSeat);
    centredFor.current = viewKey;
  }, [fit, view, layoutKey]);
  // After a hand-set zoom lands, scroll so the anchor point stays put.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const p = pendingScroll.current;
    if (!canvas) return;
    if (!p) {
      // A fit that landed on the floor (not a hand-set zoom): centre on the top row.
      if (fitRef.current && zoom <= fitFloor(panelRef.current !== null) + 0.001)
        centreOnTop(canvas, panelRef.current);
      return;
    }
    pendingScroll.current = null;
    const k = zoom / p.from;
    canvas.scrollLeft = (p.sl + p.ax) * k - p.ax;
    canvas.scrollTop = (p.st + p.ay) * k - p.ay;
  }, [zoom]);
  // CHART-KB: the team enclosure follows its seats' cards (measured after every layout).
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measured whenever the tree is re-laid out (layoutKey) or zoomed
  useLayoutEffect(() => {
    const stage = stageRef.current;
    const chartData = chartQ.data;
    if (!enclosed || !stage || !chartData || view !== "tree") {
      setEnclosure(null);
      return;
    }
    const members = teamSeats(chartData, enclosed);
    const box = stage.getBoundingClientRect();
    let left = Number.POSITIVE_INFINITY;
    let top = Number.POSITIVE_INFINITY;
    let right = Number.NEGATIVE_INFINITY;
    let bottom = Number.NEGATIVE_INFINITY;
    for (const li of stage.querySelectorAll<HTMLElement>("[data-seat]")) {
      const id = li.dataset.seat;
      if (!id || !members.has(id)) continue;
      const card = li.querySelector<HTMLElement>(":scope > article");
      if (!card) continue;
      const r = card.getBoundingClientRect();
      left = Math.min(left, r.left);
      top = Math.min(top, r.top);
      right = Math.max(right, r.right);
      bottom = Math.max(bottom, r.bottom);
    }
    if (!Number.isFinite(left) || right - left <= 0) {
      setEnclosure(null);
      return;
    }
    const pad = 12;
    setEnclosure({
      left: left - box.left - pad,
      top: top - box.top - pad - 6,
      width: right - left + pad * 2,
      height: bottom - top + pad * 2 + 6,
    });
  }, [enclosed, layoutKey, zoom, view]);
  const zoomTo = (next: number, anchor?: { x: number; y: number }) => {
    const canvas = canvasRef.current;
    const z = clampZoom(next);
    setFit(false);
    if (canvas && z !== zoomRef.current) {
      pendingScroll.current = {
        sl: canvas.scrollLeft,
        st: canvas.scrollTop,
        ax: anchor?.x ?? canvas.clientWidth / 2,
        ay: anchor?.y ?? canvas.clientHeight / 2,
        from: zoomRef.current,
      };
    }
    setZoom(z);
  };
  // ⌘ / Ctrl + wheel zooms at the pointer (a native listener: React's wheel handler is passive,
  // and the browser's own page zoom must not run too).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || view !== "tree") return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const r = canvas.getBoundingClientRect();
      zoomTo(zoomRef.current * Math.exp(-e.deltaY * 0.002), {
        x: e.clientX - r.left,
        y: e.clientY - r.top,
      });
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  });
  // Dragging the empty canvas (not a card, a button or a link) pans it.
  const pan = useRef<{ id: number; x: number; y: number; sl: number; st: number } | null>(null);
  const [panning, setPanning] = useState(false);
  const onCanvasPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || view !== "tree" || e.button !== 0 || e.pointerType === "touch") return;
    if ((e.target as Element).closest("article, button, a, input, label, [data-slot='drop-top']"))
      return;
    pan.current = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      sl: canvas.scrollLeft,
      st: canvas.scrollTop,
    };
    canvas.setPointerCapture(e.pointerId);
    setPanning(true);
  };
  const onCanvasPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const canvas = canvasRef.current;
    const p = pan.current;
    if (!canvas || !p || p.id !== e.pointerId) return;
    canvas.scrollLeft = p.sl - (e.clientX - p.x);
    canvas.scrollTop = p.st - (e.clientY - p.y);
  };
  const onCanvasPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (pan.current?.id !== e.pointerId) return;
    pan.current = null;
    canvasRef.current?.releasePointerCapture(e.pointerId);
    setPanning(false);
  };

  const pickView = (v: ChartView) => {
    setViewPref(v);
    try {
      window.localStorage.setItem(VIEW_KEY, v);
    } catch {}
  };

  const roleName = new Map(roles.data?.pages.flatMap((p) => p.data).map((r) => [r.id, r.name]));
  const focusedSeat =
    focus.subject?.kind === "seat"
      ? focus.subject.id
      : deFocus
        ? des.data?.pages.flatMap((p) => p.data).find((d) => d.membership_id === deFocus)?.seat?.id
        : undefined;

  const chart = chartQ.data;
  const sup = chart ? supervisors(chart, ops) : new Map<string, string | null>();
  const lines = chart ? linesOf(chart, ops).filter((l) => visibleLines.has(l.kind)) : [];
  const moved = new Set(ops.flatMap((o) => (o.op === "reparent" ? [o.seat] : [])));
  const titleOf = (id: string) => chart?.seats.find((s) => s.id === id)?.title ?? "A seat";
  const seatAgents = agentsBySeat(chart);
  const teamOf = new Map<string, string[]>();
  for (const m of chart?.team_members ?? []) {
    const name = chart?.teams.find((t) => t.id === m.team_id)?.name;
    if (name) teamOf.set(m.seat_id, [...(teamOf.get(m.seat_id) ?? []), name]);
  }

  // Scope: a team and its sub-teams' seats.
  // TEAM-KPI: every viewable team's score for the week (the enclosure's and the panel's chip).
  const teamScoreOf = new Map((teamScores.data ?? []).map((t) => [t.team_id, t]));
  const scopeId = scopeTeam(focus.scope);
  const scopeTeamRow = scopeId ? chart?.teams.find((t) => t.id === scopeId) : undefined;
  const inScope = chart && scopeTeamRow ? teamSeats(chart, scopeTeamRow.id) : null;
  const all = (chart?.seats ?? []).filter((s) => s.status !== "closed" || moved.has(s.id));
  const scoped = inScope ? all.filter((s) => inScope.has(s.id)) : all;
  // "My line": the viewer's seats with everything above and below them.
  const lineSeats = line && chart ? myLine(chart, sup, myMemberships) : null;
  const seats = keepLine(scoped, lineSeats);
  const found = searchKeeps(seats, sup, search);
  const drawn = found ? seats.filter((s) => found.shown.has(s.id)) : seats;
  const panel = panelSeat ? chart?.seats.find((s) => s.id === panelSeat) : undefined;
  // CHART-STAFF: staff seats sit beside their supervisor's card (never collapsed with reports).
  // SHARED-DES: a shared DE's linked cards under the seats it serves (never counted there).
  const tree = seatTree(drawn, sup, {
    staffOf: (x) => x.staff,
    links: chart?.service_links ?? [],
    teams: chart?.teams ?? [],
    // SHARED-DES-2: a team filter keeps the team's shared cards (their home is outside it).
    all,
  });
  const shared = sharedSeatIds(chart);
  const seatById = new Map((chart?.seats ?? []).map((x) => [x.id, x]));
  const leadSeatIds = new Set(
    (chart?.teams ?? []).flatMap((t) => (t.lead_seat_id ? [t.lead_seat_id] : [])),
  );
  const staffIds = new Set(drawn.filter((x) => x.staff).map((x) => x.id));
  const withChildren = new Set(
    [...sup].flatMap(([id, v]) => (v !== null && !staffIds.has(id) ? [v] : [])),
  );
  const completenessSummary = dimsQ.data?.completeness.summary;

  const apply = (op: DraftOp) => {
    if (!chart) return;
    setOps((prev) => withOp(chart, prev, op));
    setNotice(null);
  };
  /** STRUCTURE-CHANGED §3.1: "Reports to…" / "Move under…" stage a move; false when refused. */
  const stageMove = (seat: string, to: string | null): boolean => {
    if (!chart) return false;
    const why = dropRefusal(chart, ops, "reports_to", seat, to);
    if (why) {
      setNotice(why);
      return false;
    }
    apply(dropOp("reports_to", seat, to));
    return true;
  };
  // A move staged elsewhere (a DE's page: `?move=&under=`) joins the draft once the chart is read,
  // and its review opens.
  const stagedFor = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: stage once per URL move, when the chart arrives
  useEffect(() => {
    if (!stage || !chart || !manager) return;
    const key = `${stage.seat}>${stage.to ?? "top"}`;
    if (stagedFor.current === key) return;
    stagedFor.current = key;
    setPanelSeat(stage.seat);
    if (stageMove(stage.seat, stage.to)) setReviewing(true);
    onStaged?.();
  }, [stage?.seat, stage?.to, chart]);
  const drop = (target: string | null) => {
    if (!dragging || !chart) return;
    const why = dropRefusal(chart, ops, dragKind, dragging, target);
    if (why) setNotice(why);
    else apply(dropOp(dragKind, dragging, target));
    setDragging(null);
    setOver(null);
  };
  const dropTarget = (target: string | null) =>
    manager && chart
      ? {
          onDragOver: (e: DragEvent) => {
            if (!dragging || dropRefusal(chart, ops, dragKind, dragging, target)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            setOver(target ?? "top");
          },
          onDragLeave: () => setOver((o) => (o === (target ?? "top") ? null : o)),
          onDrop: (e: DragEvent) => {
            e.preventDefault();
            drop(target);
          },
        }
      : {};
  const toggleBranch = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // CHART-TABLE-ORDER: the order of one supervisor's seats, written at once (no draft: it moves
  // nobody) and drawn before the answer; a refusal puts the chart back and says why.
  const chartKey = chartKeys.orgChart(company.id);
  const reorder = useMutation({
    mutationFn: (v: { parent: string | null; ids: string[] }) =>
      api.client.orgChart.putOrder({
        params: { co: company.id },
        body: { supervisor_seat_id: v.parent, seat_ids: v.ids },
      }),
    onMutate: async (v) => {
      await queryClient.cancelQueries({ queryKey: chartKey, exact: true });
      const prev = queryClient.getQueryData<OrgChart>(chartKey);
      if (prev) queryClient.setQueryData(chartKey, withOrder(prev, v.ids));
      setNotice(null);
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(chartKey, ctx.prev);
      setNotice("The new order was not saved. Try again.");
    },
    onSuccess: (next) => queryClient.setQueryData(chartKey, next),
  });
  /** The seat's supervisor as stored (null: the top), when no draft move touches it. */
  const storedParent = (id: string): string | null | undefined => {
    const s = chart?.seats.find((x) => x.id === id);
    if (!s || moved.has(id)) return undefined;
    return s.supervisor_seat_id;
  };
  const sameParent = (a: string, b: string) => {
    const pa = storedParent(a);
    return pa !== undefined && pa === storedParent(b);
  };
  const writeOrder = (parent: string | null, next: string[], before: string[]) => {
    if (next.join() !== before.join()) reorder.mutate({ parent, ids: next });
  };
  /** Move up (-1) or down (+1) among its supervisor's seats: the row menu's keyboard way. */
  const stepSeat = (id: string, delta: -1 | 1) => {
    const parent = storedParent(id);
    if (!chart || parent === undefined) return;
    const ids = storedSiblings(chart, parent).map((s) => s.id);
    writeOrder(parent, stepIds(ids, id, delta), ids);
  };
  const rowDrop = (target: string, place: DropPlace) => {
    const dragged = rowDrag;
    setRowDrag(null);
    setRowOver(null);
    if (!dragged || !chart || dragged === target) return;
    if (place === "inside") {
      // "Make a report of": the chart's own re-parent, into the same draft and review.
      const why = dropRefusal(chart, ops, "reports_to", dragged, target);
      if (why) setNotice(why);
      else apply(dropOp("reports_to", dragged, target));
      return;
    }
    const parent = storedParent(target);
    if (parent === undefined || !sameParent(dragged, target)) return;
    const ids = storedSiblings(chart, parent).map((s) => s.id);
    writeOrder(parent, reorderIds(ids, dragged, target, place), ids);
  };
  /** Where a drag over `target` would land, or null where it cannot. */
  const rowPlace = (e: DragEvent<HTMLElement>, target: string): DropPlace | null => {
    if (!rowDrag || !chart || rowDrag === target) return null;
    const r = e.currentTarget.getBoundingClientRect();
    const place = dropPlaceAt(e.clientY - r.top, r.height, sameParent(rowDrag, target));
    if (place === "inside" && dropRefusal(chart, ops, "reports_to", rowDrag, target)) return null;
    return place;
  };

  // The dimension rows of a Structure card. A dimension the server left out (not this viewer's
  // to see) draws nothing; one it answered with null reads as "Not scored" / "No cost".
  const pending = dimsQ.isPending && needsDimensions(dims);
  /** A card's "Pay hidden": the badge's icon alone, named for screen readers and on hover. */
  const payHiddenMark = (
    <span
      data-slot="pay-hidden"
      role="img"
      aria-label="Pay hidden"
      title="Pay and benefits are left out of this seat: you may not read them"
      className="inline-flex shrink-0 text-faint"
    >
      <EyeOffIcon aria-hidden="true" className="size-3" />
    </span>
  );
  const kpiRow = (d: SeatDimensions | undefined): ReactNode => {
    if (pending) return <span className="text-faint">…</span>;
    if (!d || d.kpi === undefined) return null;
    if (d.kpi === null)
      return (
        <span data-dimension-value="kpi" className="text-faint">
          Not scored
        </span>
      );
    return (
      <span data-dimension-value="kpi" className="flex min-w-0 items-center gap-1.5">
        {/* Wave 9: the card's compact badge — dot and label, the score as a bare number. */}
        <BandBadge
          band={d.kpi.band as Band}
          className="h-4 min-w-0 gap-1 border-0 bg-transparent px-0 text-2xs [&>svg]:hidden"
        />
        {d.kpi.kpi_score !== null ? (
          <span
            className="shrink-0 text-muted-foreground tabular-nums"
            title={`KPI score ${formatScore(d.kpi.kpi_score)}`}
          >
            {Math.round(d.kpi.kpi_score)}
          </span>
        ) : null}
      </span>
    );
  };
  const loadRow = (d: SeatDimensions | undefined): ReactNode => {
    if (pending) return <span className="text-faint">…</span>;
    if (!d?.load) return null;
    const tone = loadTone(d.load);
    return (
      <span
        data-dimension-value="load"
        data-tone={tone}
        className={cn(
          "truncate tabular-nums",
          tone === "danger"
            ? "text-danger-text"
            : tone === "warning"
              ? "text-warning-text"
              : d.load.open === 0
                ? "text-faint"
                : "text-secondary",
        )}
      >
        {d.load.open === 0 ? "No open tasks" : loadText(d.load)}
      </span>
    );
  };
  const costRow = (d: SeatDimensions | undefined): ReactNode => {
    if (pending) return <span className="text-faint">…</span>;
    if (!d || d.cost === undefined) return null;
    const node = d.cost;
    if (node === null)
      return (
        <span data-dimension-value="cost" className="text-faint">
          No cost
        </span>
      );
    const state = budgetState(node);
    return (
      <span
        data-dimension-value="cost"
        className="flex min-w-0 flex-1 items-center gap-1.5 tabular-nums"
      >
        <span className="truncate font-medium text-secondary">
          {node.actual_minor === 0 && node.budget_minor === null
            ? "No cost"
            : formatMoney(node.actual_minor, node.currency)}
          {node.budget_minor === null
            ? ""
            : ` of ${formatMoney(node.budget_minor, node.currency, { whole: true })}`}
        </span>
        {node.budget_minor !== null ? (
          <span
            className={cn(
              "ml-auto shrink-0",
              state.tone === "danger"
                ? "text-danger-text"
                : state.tone === "warning"
                  ? "text-warning-text"
                  : "text-muted-foreground",
            )}
            title={`${ratioText(node.utilization)} of the budget used`}
          >
            {ratioText(node.utilization)}
          </span>
        ) : null}
        {node.de_budget ? <BudgetPaceMark pace={node.de_budget} currency={node.currency} /> : null}
        {node.restricted_hidden ? payHiddenMark : null}
      </span>
    );
  };
  const responsibilitiesRow = (d: SeatDimensions | undefined): ReactNode => {
    if (pending) return <span className="text-faint">…</span>;
    if (!d?.responsibilities) return null;
    const { count, top } = d.responsibilities;
    if (count === 0)
      return (
        <span data-dimension-value="responsibilities" className="text-faint">
          None yet
        </span>
      );
    const shownRows = top.slice(0, 3);
    return (
      <span
        data-dimension-value="responsibilities"
        className="line-clamp-2 text-secondary"
        title={top.map((r) => r.name).join(", ")}
      >
        {shownRows.map((r) => r.name).join(", ")}
        {count > shownRows.length ? (
          <span className="text-faint"> +{count - shownRows.length}</span>
        ) : null}
      </span>
    );
  };
  /** The Structure card's rows: one per dimension on and answered. */
  const rowsFor = (s: ChartSeat): ReactNode => {
    const d = dimOf.get(s.id);
    const rows: [Dimension, string, ReactNode][] = [];
    if (dims.has("responsibilities"))
      rows.push(["responsibilities", "Owns", responsibilitiesRow(d)]);
    if (dims.has("kpi")) rows.push(["kpi", "KPIs", kpiRow(d)]);
    if (dims.has("load")) rows.push(["load", "Load", loadRow(d)]);
    if (dims.has("cost")) rows.push(["cost", "Cost", costRow(d)]);
    const drawnRows = rows.filter(([, , node]) => node !== null);
    if (!drawnRows.length) return null;
    return (
      <DimensionRows>
        {drawnRows.map(([key, label, node]) => (
          <DimensionRow key={key} dimension={key} label={label} density="compact">
            {node}
          </DimensionRow>
        ))}
      </DimensionRows>
    );
  };

  // Lenses (CHART-LENSES, CHART-KB): what a card says under each lens beyond its identity -----

  /** "Shown to: reporting line" — a lens the server left out of this seat for the viewer. */
  const hiddenNote = (s: ChartSeat, l: Lens = lens, className?: string): ReactNode => {
    const level = lensAudience(l, visibility);
    return (
      <span
        data-slot="lens-hidden"
        data-lens={l}
        className={cn("flex min-w-0 items-center gap-1.5 text-2xs text-faint", className)}
        title={`${LENS_LABELS[l]} of ${s.title}: shown to ${level ? LEVEL_LABELS[level].toLowerCase() : "nobody"}`}
      >
        <LockIcon aria-hidden="true" className="size-3 shrink-0" />
        <span className="truncate">
          Shown to: {level ? LEVEL_LABELS[level].toLowerCase() : "—"}
        </span>
      </span>
    );
  };
  const money = (minor: number, currency: Parameters<typeof formatMoney>[1]) =>
    formatMoney(minor, currency, { whole: true });
  /** A measurable's row: its name, then actual / target in its health's tone. */
  const measurableRow = (m: NonNullable<NonNullable<SeatDimensions["kpi"]>["top"]>[number]) => {
    // TEAM-KPI: the line's health under the metric's own bands (the company's otherwise).
    const tone = HEALTH_TEXT[lineHealth(m) ?? "none"];
    return (
      <span
        key={m.metric_def_id}
        data-slot="measurable-row"
        className="flex min-w-0 flex-1 items-center gap-2"
        title={`${m.name}: ${formatCardMetric(m.actual, m.unit)}${m.target === null ? "" : ` of ${formatCardMetric(m.target, m.unit)}`}${m.attainment === null ? "" : ` (${Math.round(m.attainment * 100)}%)`}`}
      >
        <span className="min-w-0 flex-1 truncate text-secondary">{m.name}</span>
        <span className={cn("shrink-0 tabular-nums", tone)}>
          {formatCardMetric(m.actual, m.unit)}
          {m.target !== null ? (
            <span className="text-faint"> / {formatCardMetric(m.target, m.unit)}</span>
          ) : null}
        </span>
      </span>
    );
  };
  /** The two to four rows of the lens on a seat: key, label, value. */
  const lensRows = (
    s: ChartSeat,
    d: SeatDimensions | undefined,
  ): [string, string | null, ReactNode][] => {
    if (pending)
      return [
        [
          "wait",
          null,
          <span key="w" className="text-faint">
            …
          </span>,
        ],
      ];
    switch (lens) {
      case "structure":
        return [];
      case "responsibilities": {
        const r = d?.responsibilities;
        if (!r || r.count === 0) return [];
        return [
          ["coverage", "Measured", `${r.measurable} of ${r.count}`],
          ...r.top.slice(0, 3).map((t): [string, string | null, ReactNode] => [
            `top-${t.id}`,
            null,
            <span key={t.id} className="flex min-w-0 items-center gap-1.5" title={t.name}>
              <LensBandDot band={t.measured ? "green" : "none"} />
              <span className="truncate">{t.name}</span>
            </span>,
          ]),
        ];
      }
      case "performance": {
        const k = d?.kpi;
        if (!k) return [];
        // Wave 9: no "Band" row — the header's score dot already carries the band.
        return [
          ...k.top
            .slice(0, 4)
            .map((m): [string, string | null, ReactNode] => [
              `m-${m.metric_def_id}`,
              null,
              measurableRow(m),
            ]),
        ];
      }
      case "execution": {
        const k = d?.kpi;
        if (!k) return [];
        const met = k.top.filter((m) => m.attainment !== null && m.attainment >= 1).length;
        const scored = k.top.filter((m) => m.attainment !== null).length;
        return [
          ["goal", "Goal score", <span key="g">{formatScore(k.goal_score)}</span>],
          ["met", "Targets met", `${met} of ${scored}`],
          ...k.top
            .slice(0, 2)
            .map((m): [string, string | null, ReactNode] => [
              `m-${m.metric_def_id}`,
              null,
              measurableRow(m),
            ]),
        ];
      }
      case "tasks": {
        const l = d?.load;
        if (!l) return [];
        const tone = loadTone(l);
        return [
          ["open", "Open", plural(l.open, "task")],
          [
            "overdue",
            "Overdue",
            <span
              key="o"
              className={cn(
                tone === "danger"
                  ? "text-danger-text"
                  : tone === "warning"
                    ? "text-warning-text"
                    : undefined,
              )}
            >
              {l.overdue}
            </span>,
          ],
          ...(l.estimate_minutes > 0
            ? [
                ["hours", "Estimated", `${hoursText(l.estimate_minutes)} h`] as [
                  string,
                  string,
                  ReactNode,
                ],
              ]
            : []),
        ];
      }
      case "finance": {
        const c = d?.cost;
        if (!c) return [];
        const used = c.utilization === null ? null : Math.min(100, Number(c.utilization) * 100);
        const state = budgetState(c);
        return [
          [
            "spent",
            "Spent",
            <span key="s" className="flex min-w-0 items-center gap-1.5">
              {money(c.actual_minor, c.currency)}
              {c.de_budget ? <BudgetPaceMark pace={c.de_budget} currency={c.currency} /> : null}
              {c.restricted_hidden ? payHiddenMark : null}
            </span>,
          ],
          [
            "budget",
            "Budget",
            c.budget_minor === null ? (
              <span key="nb" className="text-faint">
                No budget
              </span>
            ) : (
              money(c.budget_minor, c.currency)
            ),
          ],
          ...(used !== null
            ? [
                [
                  "bar",
                  null,
                  <InlineBar
                    key="bar"
                    value={used}
                    size="xs"
                    tone={
                      state.tone === "danger"
                        ? "danger"
                        : state.tone === "warning"
                          ? "warning"
                          : "primary"
                    }
                    aria-hidden="true"
                    className="w-full"
                  />,
                ] as [string, string | null, ReactNode],
              ]
            : []),
        ];
      }
      case "keystones": {
        const k = d?.keystones;
        if (!k || k.count === 0) return [];
        return [
          ...k.top.map((g): [string, string | null, ReactNode] => [
            `g-${g.id}`,
            null,
            <span
              key={g.id}
              data-slot="keystone-row"
              data-track={g.track}
              className="flex min-w-0 flex-1 items-center gap-1.5"
              title={`${g.title}: ${TRACK_LABELS[g.track]}, ${g.milestones_done} of ${g.milestones_total} milestones done`}
            >
              <LensBandDot band={TRACK_BAND[g.track]} aria-label={TRACK_LABELS[g.track]} />
              <span className="min-w-0 flex-1 truncate text-secondary">{g.title}</span>
              {g.milestones_total > 0 ? (
                <span className="shrink-0 text-faint tabular-nums">
                  {g.milestones_done}/{g.milestones_total}
                </span>
              ) : null}
            </span>,
          ]),
          [
            "track",
            "On track",
            <span key="t" className="tabular-nums">
              {k.on} of {k.count}
              {k.at_risk > 0 ? (
                <span className="text-warning-text"> · {k.at_risk} at risk</span>
              ) : null}
            </span>,
          ],
        ];
      }
      case "overall": {
        const o = d?.overall;
        if (!o) return [];
        const parts = SCORED_LENSES.filter((p) => lensesServed.includes(p));
        return [
          ...parts.map((p): [string, string | null, ReactNode] => {
            if (!lensVisible(p, d))
              return [
                p,
                PART_LABELS[p],
                <span key={p} className="flex items-center gap-1 text-faint">
                  <LockIcon aria-hidden="true" className="size-3" /> hidden
                </span>,
              ];
            const sc = lensScoreOf(p, s, d, thresholds);
            return [
              p,
              PART_LABELS[p],
              <LensScoreMark
                key={p}
                size="md"
                score={sc.score}
                band={sc.band}
                unit={LENS_UNITS[p]}
                {...(sc.note ? { note: sc.note } : {})}
                className="min-w-0 shrink overflow-hidden text-2xs [&>span:last-child]:truncate"
              />,
            ];
          }),
          // Wave 9: "N of 6 lenses" scored is the card's footer, not a row as well.
        ];
      }
      case "completeness": {
        const c = d?.completeness;
        if (!c) return [];
        const missing = c.missing.slice(0, 3);
        return [
          ["met", "In place", `${c.met} of ${c.required} items`],
          ...missing.map((item): [string, string | null, ReactNode] => [
            `missing-${item}`,
            null,
            <span
              key={item}
              data-slot="missing-item"
              className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden"
              title={COMPLETENESS_LABELS[item]}
            >
              <LensBandDot band="red" aria-label="Missing" />
              <span className="truncate text-secondary">{COMPLETENESS_LABELS[item]}</span>
            </span>,
          ]),
          ...(c.missing.length > missing.length || c.hidden > 0
            ? [
                [
                  "more",
                  null,
                  <span key="more" className="text-faint">
                    {c.missing.length > missing.length
                      ? `+${c.missing.length - missing.length} more`
                      : ""}
                    {c.hidden > 0
                      ? `${c.missing.length > missing.length ? " · " : ""}${plural(c.hidden, "item")} shown to others`
                      : ""}
                  </span>,
                ] as [string, string | null, ReactNode],
              ]
            : []),
        ];
      }
    }
  };
  /** The card's body under a lens: the rows, or who sees it. */
  const lensBody = (s: ChartSeat): ReactNode => {
    const d = dimOf.get(s.id);
    if (!pending && !lensVisible(lens, d)) return <DimensionRows>{hiddenNote(s)}</DimensionRows>;
    const rows = lensRows(s, d);
    if (!rows.length) return null;
    return (
      <DimensionRows data-lens-rows={lens}>
        {rows.map(([key, label, node]) => (
          <DimensionRow key={key} dimension={key} label={label} density="compact">
            {node}
          </DimensionRow>
        ))}
      </DimensionRows>
    );
  };
  /** The headline: the lens's number in its band, top right of the title. */
  const headline = (s: ChartSeat): ReactNode => {
    // Completeness: the ring beside it is the lens's number, so it is said once.
    if (lens === "structure" || lens === "completeness" || pending) return null;
    if (!lensVisible(lens, dimOf.get(s.id))) return null;
    const sc = scoreOf(s);
    return (
      <LensScoreMark
        size="md"
        score={sc.score}
        band={sc.band}
        unit={LENS_UNITS[lens]}
        {...(sc.note ? { note: sc.note } : {})}
        data-lens-score={lens}
        className="max-w-[55%] shrink-0 [&>span:last-child]:truncate"
      />
    );
  };
  /** The card header's score: the lens's headline, or under Completeness the setup ring. */
  const cardScore = (s: ChartSeat): ReactNode =>
    lens === "completeness" ? (pending ? null : ring(s)) : headline(s);
  /** CHART-KB: the seat-setup ring (a card shows it under Completeness only, W9-FIXES). */
  const ring = (s: ChartSeat, size: "sm" | "lg" = "sm"): ReactNode => {
    const c = dimOf.get(s.id)?.completeness;
    if (!c) return null;
    return (
      <CompletenessRing
        score={c.score}
        band={c.score >= 100 ? "green" : c.score >= 80 ? "amber" : "red"}
        size={size}
        label={`Seat setup ${c.score}% complete: ${c.met} of ${c.required} items in place`}
        data-slot="card-ring"
      />
    );
  };
  /** CHART-KB: the thin bars along a card's top: one per lens the seat carries. */
  const bars = (s: ChartSeat): ReactNode => {
    const d = dimOf.get(s.id);
    if (pending || !d) return null;
    const shownBars = SCORING_LENSES.filter((l) => lensesServed.includes(l) && lensVisible(l, d));
    if (!shownBars.length) return null;
    return (
      <DimensionBars
        className="absolute inset-x-2 top-0"
        bars={shownBars.map((l) => ({
          lens: l,
          label: LENS_LABELS[l],
          state: l === lens ? "active" : lensCarried(l, d) ? "has" : "empty",
        }))}
      />
    );
  };
  /** "4 measurements · View all": what the lens counts on the seat, opening the panel. */
  const footerText = (s: ChartSeat): string | null => {
    const d = dimOf.get(s.id);
    if (pending || !d || !lensVisible(lens, d)) return null;
    switch (lens) {
      case "structure":
      case "responsibilities":
        return plural(d.responsibilities?.count ?? 0, "responsibility").replace(
          "responsibilitys",
          "responsibilities",
        );
      case "performance":
      case "execution":
        return plural(d.kpi?.metrics ?? 0, "measurement");
      case "tasks":
        return plural(d.load?.open ?? 0, "open task");
      case "finance":
        return "Budget vs actual";
      case "keystones":
        return plural(d.keystones?.count ?? 0, "priority").replace("prioritys", "priorities");
      case "overall":
        return `${d.overall?.scored ?? 0} of ${d.overall?.total ?? 0} lenses`;
      case "completeness":
        return `${d.completeness?.met ?? 0} of ${d.completeness?.required ?? 0} items`;
    }
  };
  /** A manager's branch roll-up: the mean under it, with the seats counted. */
  const branchLine = (node: SeatNode): ReactNode => {
    const b = branchOf(lens, node, scoreOf, thresholds);
    if (!b || b.score === null) return null;
    return (
      <span
        data-slot="lens-branch"
        data-band={b.band}
        className="flex items-center gap-1.5 truncate text-2xs text-muted-foreground tabular-nums"
        title={`Branch ${LENS_LABELS[lens].toLowerCase()}: the mean of ${plural(b.seats, "seat")} under ${node.seat.title}`}
      >
        <LensBandDot band={b.band} />
        Branch {b.score}
        {LENS_UNITS[lens]} · {plural(b.seats, "seat")}
      </span>
    );
  };

  /** A seat's card and what hangs off it; `asStaff`: drawn at the side of its supervisor. */
  const seatCard = (node: SeatNode, asStaff = false): ReactNode => {
    const s = node.seat;
    const focused = s.id === focusedSeat;
    const composition = seatComposition(s);
    const out = lines.filter((l) => l.from === s.id);
    const open = !collapsed.has(s.id) || Boolean(found);
    const agents = seatAgents.get(s.id) ?? [];
    const agentsShown = agents.length > 0 && agentsOpen.has(s.id);
    const teams = teamOf.get(s.id) ?? [];
    const unmatched = found ? !found.matched.has(s.id) : false;
    const d = dimOf.get(s.id);
    // Nothing in the lens on this seat (no open tasks, no priorities, no scorecard): dimmed.
    const empty = lens !== "structure" && !pending && lensVisible(lens, d) && !lensCarried(lens, d);
    const actions = (
      <span
        data-slot="seat-actions"
        className="absolute -top-7 right-2 z-10 flex shrink-0 items-center gap-0.5 rounded-md border border-border bg-card p-0.5 opacity-0 shadow-sm transition-opacity duration-(--dur-fast) group-hover/card:opacity-100 group-focus-within/card:opacity-100 pointer-coarse:opacity-100"
      >
        <Act
          tip="Open"
          render={
            <Link
              to="/c/$slug/kpis/chart/seat/$id"
              params={{ slug: company.slug, id: s.id }}
              search={keepFocus}
              aria-label={`Open ${s.title}`}
              className={cn(buttonVariants({ variant: "ghost", size: "icon-xs" }))}
            />
          }
        >
          <ArrowUpRightIcon aria-hidden="true" />
        </Act>
        <Act
          tip={panelSeat === s.id ? "Close details" : "Details"}
          render={
            <Button
              variant="ghost"
              size="icon-xs"
              aria-pressed={panelSeat === s.id}
              aria-label={`Details of ${s.title}`}
              aria-controls="seat-panel"
              className="aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
              onClick={() => setPanelSeat((cur) => (cur === s.id ? null : s.id))}
            />
          }
        >
          <PanelRightOpenIcon aria-hidden="true" />
        </Act>
        <Act
          tip={focused ? "Unfocus" : "Focus"}
          render={
            <Button
              variant="ghost"
              size="icon-xs"
              aria-pressed={focused}
              aria-label={focused ? `Unfocus ${s.title}` : `Focus ${s.title}`}
              className="aria-pressed:bg-primary-soft aria-pressed:text-primary-text max-md:hidden"
              onClick={() => setFocus({ subject: focused ? null : { kind: "seat", id: s.id } })}
            />
          }
        >
          <CrosshairIcon aria-hidden="true" />
        </Act>
        {manager ? (
          <>
            <Act
              tip="Add a report"
              render={
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Add a report to ${s.title}`}
                  className="max-md:hidden"
                  onClick={() => setAdding(s.id)}
                />
              }
            >
              <UserPlusIcon aria-hidden="true" />
            </Act>
            <Act
              tip="Lines"
              render={
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Lines of ${s.title}`}
                  onClick={() => setLinesFor(s.id)}
                />
              }
            >
              <GitBranchPlusIcon aria-hidden="true" />
            </Act>
            {/* MODALS: Setup's seat drawer over the chart, not Setup › Seats. */}
            <Act
              tip="Edit"
              render={
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Edit ${s.title}`}
                  className="max-md:hidden"
                  onClick={() => setEditing(s.id)}
                />
              }
            >
              <PencilIcon aria-hidden="true" />
            </Act>
          </>
        ) : null}
      </span>
    );
    const chips = (
      <>
        {out.map((l) => (
          <Badge
            key={`${l.kind}-${l.to}`}
            variant={l.kind === "authority" ? "warning" : "info"}
            data-line={l.kind}
            className="max-w-full gap-1"
            title={`${LINE_VERBS[l.kind]} ${titleOf(l.to)}`}
          >
            <span className="truncate">
              {LINE_VERBS[l.kind]} {titleOf(l.to)}
            </span>
            {manager ? (
              <button
                type="button"
                aria-label={`Remove: ${titleOf(s.id)} ${LINE_VERBS[l.kind].toLowerCase()} ${titleOf(l.to)}`}
                className="-mr-1 inline-flex size-4 items-center justify-center rounded-sm hover:bg-active"
                onClick={() => apply({ op: "remove", kind: l.kind, from: s.id, to: l.to })}
              >
                <XIcon aria-hidden="true" className="size-3" />
              </button>
            ) : null}
          </Badge>
        ))}
        {dims.has("team") && lens === "structure"
          ? teams.slice(0, 2).map((name) => (
              <Badge key={name} variant="outline" data-dimension="team" className="max-w-36">
                <span className="truncate">{name}</span>
              </Badge>
            ))
          : null}
        {/* CHART-STAFF: says it in words too (the side line is visual only). */}
        {asStaff ? (
          <Badge
            variant="outline"
            data-slot="staff-chip"
            title={`Staff of ${titleOf(sup.get(s.id) ?? "")}`}
          >
            Staff
            <span className="sr-only"> of {titleOf(sup.get(s.id) ?? "")}</span>
          </Badge>
        ) : null}
        {composition === "mixed" || composition === "digital" ? (
          <Badge variant="accent" data-composition={composition}>
            {COMPOSITION_LABELS[composition]}
          </Badge>
        ) : composition === "human" ? (
          <span className="sr-only" data-composition="human">
            Human
          </span>
        ) : null}
        {/* "Open" is the dashed card and its "Open seat" name; the other states get a chip. */}
        {s.status !== "filled" && !(s.status === "open" && composition === "open") ? (
          <Badge variant={SEAT_STATUS_TONE[s.status]}>{SEAT_STATUS_LABELS[s.status]}</Badge>
        ) : null}
        {/* SHARED-DES: the home card of a shared DE (its linked cards point here). */}
        {shared.has(s.id) ? <SharedBadge /> : null}
      </>
    );
    const branchToggle = node.children.length ? (
      <button
        type="button"
        aria-expanded={open}
        aria-label={`${open ? "Collapse" : "Expand"} ${s.title}: ${plural(node.children.length, "report")}`}
        onClick={() => toggleBranch(s.id)}
        className="inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-sm px-1 text-2xs text-muted-foreground tabular-nums hover:bg-active hover:text-foreground"
      >
        {/* Wave 9 (KPI Beast's "👥 2 ⌃" chip): the count, so the footer keeps its words. */}
        <UsersIcon aria-hidden="true" className="size-3" />
        {node.children.length}
        {open ? (
          <ChevronDownIcon aria-hidden="true" className="size-3" />
        ) : (
          <ChevronRightIcon aria-hidden="true" className="size-3" />
        )}
      </button>
    ) : null;
    const footer = footerText(s);
    const hasChips =
      asStaff ||
      out.length ||
      (lens === "structure" && dims.has("team") && teams.length) ||
      composition !== "human" ||
      s.status !== "filled" ||
      shared.has(s.id);
    const article = (
      // biome-ignore lint/a11y/useKeyWithClickEvents: the card's "Details" hover action is the keyboard way to its panel; the click is a shortcut.
      <article
        aria-label={s.title}
        draggable={manager && s.status !== "closed"}
        onDragStart={(e) => {
          e.dataTransfer.setData("text/plain", s.id);
          e.dataTransfer.effectAllowed = "move";
          setDragging(s.id);
        }}
        onDragEnd={() => {
          setDragging(null);
          setOver(null);
        }}
        {...dropTarget(s.id)}
        onClick={(e) => {
          // A click on the card itself (not a link, a button or the grip) opens its panel.
          if ((e.target as Element).closest("a, button, input, [data-slot='seat-actions']")) return;
          setPanelSeat((cur) => (cur === s.id ? null : s.id));
        }}
        data-state={focused ? "selected" : undefined}
        data-panel={panelSeat === s.id ? "" : undefined}
        data-drop={over === s.id ? "over" : undefined}
        data-moved={moved.has(s.id) ? "" : undefined}
        data-dragging={dragging === s.id ? "" : undefined}
        data-match={found?.matched.has(s.id) ? "" : undefined}
        data-open-seat={s.holders.length === 0 ? "" : undefined}
        // CHART-LENSES: the lens's hue as the card's rule and bars (lens.css); the band for tests.
        data-lens={lens}
        data-band={lens !== "structure" ? scoreOf(s).band : undefined}
        data-dim={empty ? "" : undefined}
        className={cn(
          "group/card relative flex w-[184px] min-w-0 cursor-pointer flex-col gap-1 rounded-lg border border-border bg-card px-2 pt-2 pb-1.5 shadow-xs transition-colors duration-(--dur-fast) hover:border-strong hover:shadow-sm data-[drop=over]:border-primary data-[drop=over]:bg-primary-soft data-[state=selected]:border-primary data-[state=selected]:bg-primary-soft data-dragging:opacity-40 data-moved:border-dashed data-moved:border-primary data-open-seat:border-dashed data-open-seat:border-strong data-panel:ring-2 data-panel:ring-primary/40",
          unmatched && "opacity-60",
        )}
      >
        {bars(s)}
        {manager ? (
          <GripVerticalIcon
            aria-hidden="true"
            className="absolute top-3 -left-0.5 size-4 shrink-0 cursor-grab text-faint opacity-0 group-hover/card:opacity-100 pointer-coarse:opacity-100 max-md:hidden"
          />
        ) : null}
        {/* The header row: the active lens's own score only (the setup ring is Completeness's
            score; Structure has none), then the title, in bold, on its own line. W9-FIXES
            (founder): no lens name — the selected tab says it, and at 184 px it read
            "PERFORMA…" — and no ring outside Completeness ("SEAT SETUP 100%" on every card). */}
        {cardScore(s) !== null ? (
          <div className="flex min-w-0 items-center gap-1.5" data-slot="seat-card-header">
            {cardScore(s)}
          </div>
        ) : null}
        <Link
          to="/c/$slug/kpis/chart/seat/$id"
          params={{ slug: company.slug, id: s.id }}
          search={keepFocus}
          data-slot="card-title"
          className="-mt-0.5 min-w-0 truncate text-xs leading-4 font-semibold text-foreground underline-offset-4 hover:underline"
          title={s.title}
        >
          {s.title}
        </Link>
        {/* The holder with a small avatar; an open seat reads as such. */}
        <div className="flex min-w-0 items-center gap-1.5" data-slot="card-holder">
          <SeatAvatar seat={s} size="xs" />
          <span
            className={cn(
              "truncate text-xs leading-4",
              s.holders.length === 0 ? "text-faint" : "text-muted-foreground",
            )}
          >
            {seatNameText(s)}
          </span>
          {agents.length ? (
            <SeatAgentsChip
              seat={s}
              count={agents.length}
              open={agentsShown}
              onToggle={() => toggleAgents(s.id)}
            />
          ) : null}
        </div>
        {agentsShown ? (
          <SeatAgentsList seatId={s.id} agents={agents} companySlug={company.slug} />
        ) : null}
        {hasChips ? (
          <span className="flex min-w-0 flex-wrap items-center gap-1">{chips}</span>
        ) : null}
        {/* DE-SEAT-OWNER: a DE acting in or leading from the seat, and the person who answers. */}
        {deHeld(s) ? (
          <span className="flex min-w-0 flex-col gap-0.5" data-slot="de-owner">
            {ownerChip(s, leadSeatIds) ? (
              <span className="flex flex-wrap items-center gap-1">
                <OwnerChipBadge chip={ownerChip(s, leadSeatIds) as OwnerChip} />
              </span>
            ) : null}
            <AccountableLine name={accountableName(s, seatById)} />
          </span>
        ) : null}
        {lens === "structure" ? rowsFor(s) : lensBody(s)}
        {node.children.length + node.staff.length && lens !== "structure" ? branchLine(node) : null}
        {footer !== null || node.children.length ? (
          <span className="flex min-w-0 items-center justify-between gap-2 border-t border-subtle pt-1.5">
            {footer !== null ? (
              <button
                type="button"
                data-slot="card-footer"
                className="inline-flex min-w-0 items-center gap-1 truncate text-2xs text-muted-foreground hover:text-primary-text"
                onClick={() => setPanelSeat(s.id)}
              >
                <span className="truncate">{footer}</span>
                <span className="shrink-0 text-faint">·</span>
                <span className="shrink-0 text-primary-text">View all</span>
              </button>
            ) : (
              <span />
            )}
            {branchToggle}
          </span>
        ) : null}
        {actions}
      </article>
    );
    // SHARED-DES: the linked cards follow the line reports (and the staff at the side).
    const linkedCards = node.linked.flatMap((l) => {
      const home = seatById.get(l.seatId);
      if (!home) return [];
      return [
        <li key={`linked-${l.linkId}`} data-linked-seat={home.id}>
          <LinkedCard
            home={home}
            homeTeam={homeTeamName(chart, home.id)}
            linkId={l.linkId}
            manager={manager}
            selected={panelSeat === home.id}
            onOpen={() => setPanelSeat(home.id)}
            onRefuseDrag={() => setNotice(LINKED_DRAG_REFUSAL)}
            drop={dropTarget(home.id)}
          />
        </li>,
      ];
    });
    const reports = node.children.length + linkedCards.length > 0 && open;
    // CHART-STAFF: staff (assistant) seats hang off the card's stem to the side, like a classic
    // org chart's staff line, above the row of line reports (org-tree.css `data-staff-host`).
    // They stay shown when the reports are collapsed: they belong with the card.
    const staffSide = node.staff.length ? (
      <div data-slot="seat-staff">{node.staff.map((n) => seatCard(n, true))}</div>
    ) : null;
    const Item = asStaff ? "div" : "li";
    return (
      <Item
        key={s.id}
        data-seat={s.id}
        data-title={s.title}
        data-staff-seat={asStaff ? "" : undefined}
        data-staff-host={node.staff.length ? (reports ? "through" : "end") : undefined}
      >
        {article}
        {staffSide}
        {reports ? (
          // The stem from the card down to the children's crossbar (or to an only child), on
          // every level and in every lens: org-tree.css draws it with `ul ul::before`, which
          // misses the first level (a root's children are the tree's first nested `ul`), so a
          // root such as an open CEO seat stood unconnected above its reports.
          <ul data-slot="seat-children" className={SEAT_STEM}>
            {node.children.map((n) => seatCard(n))}
            {linkedCards}
          </ul>
        ) : null}
      </Item>
    );
  };

  // CHART-KB: the Table view — the seats (in tree order, indented) × every lens's value.
  const tableLenses = SCORING_LENSES.filter((l) => lensesServed.includes(l) && !locked(l));
  // CHART-TABLE-ORDER: the rows in the chart's order (staff first, as the chart puts them beside
  // the card), a collapsed branch's rows left out (a search shows every match).
  type LinkedRow = { linkId: string; home: ChartSeat; depth: number };
  // SHARED-DES: a linked row under the served seat, after its reports; never counted as a seat.
  const tableRows: {
    node: SeatNode;
    depth: number;
    parent: string | null;
    linked?: LinkedRow;
  }[] = [];
  const walk = (nodes: SeatNode[], depth: number, parent: string | null) => {
    for (const n of nodes) {
      tableRows.push({ node: n, depth, parent });
      if (!collapsed.has(n.seat.id) || found) {
        walk([...n.staff, ...n.children], depth + 1, n.seat.id);
        for (const l of n.linked) {
          const home = seatById.get(l.seatId);
          if (home)
            tableRows.push({
              node: n,
              depth: depth + 1,
              parent: n.seat.id,
              linked: { linkId: l.linkId, home, depth: depth + 1 },
            });
        }
      }
    }
  };
  if (chart) walk(tree, 0, null);
  // The header's count: seats drawn, the shared links apart (they are no seats).
  const linkCount = tree
    .flatMap((n) => [n, ...underOf(n)])
    .reduce((k, n) => k + n.linked.length, 0);
  const countText = `${plural(drawn.length, "seat")}${linkCount ? ` · ${plural(linkCount, "shared link")}` : ""}`;
  /** Scrolls to the home row and flashes it. */
  const flashHome = (homeId: string) => {
    const tr = document.querySelector<HTMLElement>(
      `[data-slot="chart-table"] tr[data-seat="${homeId}"]`,
    );
    if (!tr) return;
    tr.scrollIntoView({ block: "center", behavior: "smooth" });
    tr.dataset.flash = "";
    window.setTimeout(() => {
      delete tr.dataset.flash;
    }, 1200);
  };
  const linkedRow = (parent: string, r: LinkedRow): ReactNode => {
    const level = Math.min(r.depth, MAX_DEPTH);
    const homeTeam = homeTeamName(chart, r.home.id);
    return (
      <tr
        key={`linked-${r.linkId}-${parent}`}
        data-linked-home={r.home.id}
        data-parent={parent}
        className="group/row hover:bg-sunken"
      >
        <td className="relative border-b border-subtle py-1.5 pr-2">
          {GUIDE_LEFT.slice(0, level).map((left) => (
            <span
              key={left}
              aria-hidden="true"
              data-slot="table-guide"
              className={cn("pointer-events-none absolute inset-y-0 w-px bg-border", left)}
            />
          ))}
          <span className={cn("flex min-w-0 items-center gap-2", DEPTH_PAD[level])}>
            <span aria-hidden="true" className="size-4 shrink-0" />
            <SeatAvatar seat={r.home} size="xs" />
            <button
              type="button"
              className="truncate text-left text-muted-foreground underline-offset-4 hover:underline"
              aria-label={`Open ${deNameOf(r.home)}'s home seat`}
              onClick={() => setPanelSeat(r.home.id)}
            >
              {deNameOf(r.home)}
            </button>
            <SharedBadge home={homeTeam} />
          </span>
        </td>
        <td className="border-b border-subtle px-2 py-1.5 text-muted-foreground max-md:hidden">
          {deNameOf(r.home)}
        </td>
        {tableLenses.map((l) => (
          <td
            key={l}
            data-lens-cell={l}
            className="border-b border-subtle px-2 py-1.5 text-right text-faint"
          >
            <button
              type="button"
              data-slot="linked-home-link"
              className="rounded-sm hover:text-primary-text hover:underline"
              title={`${LENS_LABELS[l]} shows on the home row`}
              onClick={() => flashHome(r.home.id)}
            >
              → home
            </button>
          </td>
        ))}
      </tr>
    );
  };
  const tableView = (
    <table
      data-slot="chart-table"
      className="w-full min-w-max border-separate border-spacing-0 text-xs"
    >
      <thead className="sticky top-0 z-10 bg-card">
        <tr>
          <th
            scope="col"
            className="min-w-[300px] border-b border-border py-2 pr-2 pl-7 text-left font-medium text-muted-foreground"
          >
            Seat
            <span data-slot="table-count" className="ml-2 font-normal text-faint tabular-nums">
              {countText}
            </span>
          </th>
          <th
            scope="col"
            className="border-b border-border px-2 py-2 text-left font-medium text-muted-foreground max-md:hidden"
          >
            Holder
          </th>
          {tableLenses.map((l) => (
            <th
              key={l}
              scope="col"
              data-lens={l}
              className="border-b border-border px-2 py-2 text-right font-medium whitespace-nowrap text-muted-foreground"
            >
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden="true" className="size-2 rounded-[2px] bg-(--lens)" />
                {LENS_LABELS[l]}
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {tableRows.map(({ node, depth, parent, linked }) => {
          if (linked) return linkedRow(node.seat.id, linked);
          const s = node.seat;
          const d = dimOf.get(s.id);
          const kids = node.staff.length + node.children.length;
          const open = !collapsed.has(s.id) || Boolean(found);
          const level = Math.min(depth, MAX_DEPTH);
          const movable = manager && s.status !== "closed";
          // The keyboard way: its place among the seats that report where it does (as stored).
          const sibs =
            chart && storedParent(s.id) !== undefined
              ? storedSiblings(chart, storedParent(s.id) ?? null).map((x) => x.id)
              : [];
          const at = sibs.indexOf(s.id);
          return (
            <tr
              key={s.id}
              data-seat={s.id}
              data-title={s.title}
              data-parent={parent ?? ""}
              data-state={s.id === focusedSeat ? "selected" : undefined}
              data-drop={rowOver?.id === s.id ? rowOver.place : undefined}
              data-dragging={rowDrag === s.id ? "" : undefined}
              data-moved={moved.has(s.id) ? "" : undefined}
              {...(manager
                ? {
                    onDragOver: (e: DragEvent<HTMLTableRowElement>) => {
                      const place = rowPlace(e, s.id);
                      if (!place) {
                        setRowOver((o) => (o?.id === s.id ? null : o));
                        return;
                      }
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      setRowOver((o) =>
                        o?.id === s.id && o.place === place ? o : { id: s.id, place },
                      );
                    },
                    onDragLeave: (e: DragEvent<HTMLTableRowElement>) => {
                      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                      setRowOver((o) => (o?.id === s.id ? null : o));
                    },
                    onDrop: (e: DragEvent<HTMLTableRowElement>) => {
                      e.preventDefault();
                      const place = rowPlace(e, s.id);
                      if (place) rowDrop(s.id, place);
                      else {
                        setRowDrag(null);
                        setRowOver(null);
                      }
                    },
                  }
                : {})}
              className={cn(
                "group/row hover:bg-sunken data-[state=selected]:bg-primary-soft data-dragging:opacity-40 data-flash:bg-primary-soft",
                // The drop indicator: a line between rows to reorder, a highlight to report to it.
                "data-[drop=before]:[&>td]:shadow-[inset_0_2px_0_var(--primary)] data-[drop=after]:[&>td]:shadow-[inset_0_-2px_0_var(--primary)] data-[drop=inside]:bg-primary-soft data-[drop=inside]:[&>td]:shadow-[inset_0_1px_0_var(--primary),inset_0_-1px_0_var(--primary)]",
              )}
            >
              <td className="relative border-b border-subtle py-1.5 pr-2">
                {/* Faint guide lines, one per level above the row. */}
                {GUIDE_LEFT.slice(0, level).map((left) => (
                  <span
                    key={left}
                    aria-hidden="true"
                    data-slot="table-guide"
                    className={cn("pointer-events-none absolute inset-y-0 w-px bg-border", left)}
                  />
                ))}
                {movable ? (
                  <button
                    type="button"
                    draggable
                    data-slot="row-grip"
                    aria-label={`Drag ${s.title}`}
                    title="Drag to reorder, or onto a seat to report to it"
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", s.id);
                      e.dataTransfer.effectAllowed = "move";
                      const tr = e.currentTarget.closest("tr");
                      if (tr) e.dataTransfer.setDragImage(tr, 24, 16);
                      setRowDrag(s.id);
                    }}
                    onDragEnd={() => {
                      setRowDrag(null);
                      setRowOver(null);
                    }}
                    className="absolute top-1/2 left-1.5 flex size-5 -translate-y-1/2 cursor-grab items-center justify-center rounded-sm text-faint opacity-0 hover:bg-active hover:text-foreground focus-visible:opacity-100 group-hover/row:opacity-100 pointer-coarse:opacity-100 max-md:hidden"
                  >
                    <GripVerticalIcon aria-hidden="true" className="size-4" />
                  </button>
                ) : null}
                <span className={cn("flex min-w-0 items-center gap-2", DEPTH_PAD[level])}>
                  {kids > 0 ? (
                    <button
                      type="button"
                      data-slot="row-toggle"
                      aria-expanded={open}
                      aria-label={`${open ? "Collapse" : "Expand"} ${s.title}`}
                      onClick={() => toggleBranch(s.id)}
                      className="-mx-0.5 flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-active hover:text-foreground"
                    >
                      {open ? (
                        <ChevronDownIcon aria-hidden="true" className="size-3.5" />
                      ) : (
                        <ChevronRightIcon aria-hidden="true" className="size-3.5" />
                      )}
                    </button>
                  ) : (
                    <span aria-hidden="true" className="size-4 shrink-0" />
                  )}
                  <SeatAvatar seat={s} size="xs" />
                  <Link
                    to="/c/$slug/kpis/chart/seat/$id"
                    params={{ slug: company.slug, id: s.id }}
                    search={keepFocus}
                    className="truncate font-medium underline-offset-4 hover:underline"
                  >
                    {s.title}
                  </Link>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Details of ${s.title}`}
                    aria-controls="seat-panel"
                    aria-pressed={panelSeat === s.id}
                    className="aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
                    onClick={() => setPanelSeat((cur) => (cur === s.id ? null : s.id))}
                  >
                    <PanelRightOpenIcon aria-hidden="true" />
                  </Button>
                  {manager ? (
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`Lines of ${s.title}`}
                      onClick={() => setLinesFor(s.id)}
                    >
                      <GitBranchPlusIcon aria-hidden="true" />
                    </Button>
                  ) : null}
                  {movable ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        render={
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            aria-label={`More for ${s.title}`}
                            data-slot="row-menu"
                            className="opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 aria-expanded:opacity-100 pointer-coarse:opacity-100"
                          />
                        }
                      >
                        <EllipsisIcon aria-hidden="true" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        {/* STRUCTURE-CHANGED §3.1: re-parent by hand, into the chart's draft. */}
                        <DropdownMenuItem onClick={() => setMoving(s.id)}>
                          <GitBranchPlusIcon aria-hidden="true" />
                          Move under…
                        </DropdownMenuItem>
                        {at >= 0 && sibs.length > 1 ? (
                          <>
                            <DropdownMenuItem
                              disabled={at === 0}
                              onClick={() => stepSeat(s.id, -1)}
                            >
                              <ArrowUpIcon aria-hidden="true" />
                              Move up
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={at === sibs.length - 1}
                              onClick={() => stepSeat(s.id, 1)}
                            >
                              <ArrowDownIcon aria-hidden="true" />
                              Move down
                            </DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </span>
              </td>
              <td
                className={cn(
                  "border-b border-subtle px-2 py-1.5 max-md:hidden",
                  s.holders.length === 0 && "text-faint",
                )}
              >
                {seatNameText(s)}
              </td>
              {tableLenses.map((l) => {
                if (pending)
                  return (
                    <td
                      key={l}
                      className="border-b border-subtle px-2 py-1.5 text-right text-faint"
                    >
                      …
                    </td>
                  );
                if (!lensVisible(l, d))
                  return (
                    <td key={l} className="border-b border-subtle px-2 py-1.5 text-right">
                      <span
                        className="inline-flex items-center gap-1 text-faint"
                        title="Shown to others"
                      >
                        <LockIcon aria-hidden="true" className="size-3" />
                      </span>
                    </td>
                  );
                const sc = lensScoreOf(l, s, d, thresholds);
                return (
                  <td
                    key={l}
                    data-lens-cell={l}
                    className="border-b border-subtle px-2 py-1.5 text-right tabular-nums"
                  >
                    <LensScoreMark
                      size="md"
                      score={sc.score}
                      band={sc.band}
                      unit={LENS_UNITS[l]}
                      {...(sc.note ? { note: sc.note } : {})}
                      className="justify-end text-xs [&_[data-slot=lens-unscored]>span:last-child]:sr-only"
                    />
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const lineSeat = linesFor ? chart?.seats.find((s) => s.id === linesFor) : undefined;
  const seatsLink = (
    <Link
      to="/c/$slug/setup/seats"
      params={{ slug: company.slug }}
      search={(prev: Record<string, unknown>) => ({
        ...keepFocus(prev),
        ...(focusedSeat ? { seat: focusedSeat } : {}),
      })}
      className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "max-md:hidden")}
    >
      <ArmchairIcon aria-hidden="true" />
      Seats
    </Link>
  );
  // CHART-KB: "Seat setup: 78% complete" — over the seats whose setup the viewer may see.
  const setupChip =
    completenessSummary && completenessSummary.seats > 0 ? (
      <Button
        variant="ghost"
        size="sm"
        data-slot="setup-chip"
        aria-pressed={lens === "completeness"}
        className="gap-2 font-normal text-muted-foreground aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
        title={`${completenessSummary.complete} of ${plural(completenessSummary.seats, "seat")} fully set up; the mean is ${completenessSummary.mean}%. Open the Completeness lens.`}
        onClick={() => pickLens("completeness")}
        disabled={locked("completeness")}
      >
        <CompletenessRing
          score={completenessSummary.mean}
          band={
            completenessSummary.mean >= 100
              ? "green"
              : completenessSummary.mean >= 80
                ? "amber"
                : "red"
          }
          label={`Seat setup ${completenessSummary.mean}% complete`}
          className="[&>span:last-child]:hidden"
        />
        <span className="max-md:hidden">Seat setup: {completenessSummary.mean}% complete</span>
        <span className="md:hidden">{completenessSummary.mean}%</span>
      </Button>
    ) : null;

  const header = (
    <LocationHeader
      crumbs={[chartCrumb(company)]}
      icon={NetworkIcon}
      title="Org chart"
      {...(chart ? { count: seats.length } : {})}
      actions={
        <>
          {setupChip}
          {manager ? (
            <>
              <Link
                to="/c/$slug/setup/teams"
                params={{ slug: company.slug }}
                search={keepFocus}
                className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "max-md:hidden")}
              >
                <UsersIcon aria-hidden="true" />
                Teams
              </Link>
              {seatsLink}
              <Button size="sm" onClick={() => setAdding(null)} disabled={!roles.data}>
                <PlusIcon data-icon="inline-start" />
                New seat
              </Button>
            </>
          ) : null}
        </>
      }
    />
  );

  if (chartQ.isPending)
    return (
      <ChartFrame data-screen="chart">
        {header}
        <ChartBody>
          <Loading label="Loading the org chart" rows={5} />
        </ChartBody>
      </ChartFrame>
    );
  // 05 `org_chart#viewer`: managers and seat holders. Anyone else is refused by the server.
  if (chartQ.isError && chartQ.error instanceof ApiError && chartQ.error.status === 403)
    return <NoAccessPage what="The org chart" slug={company.slug} />;
  if (chartQ.isError || !chart)
    return (
      <ChartFrame data-screen="chart">
        {header}
        <ChartBody>
          <LoadError what="The org chart" error={chartQ.error} />
        </ChartBody>
      </ChartFrame>
    );

  const steps = lensSteps(lens, thresholds);
  const periodLabel =
    grain === "week"
      ? `W${week.slice(-2)} · ${weekRangeLabel(week)}`
      : grain === "month"
        ? dimsQ.data
          ? monthLabel(dimsQ.data.month)
          : "…"
        : (dimsQ.data?.completeness.quarter ?? "…");
  const quiet = cn(buttonVariants({ variant: "ghost", size: "icon-sm" }), "text-muted-foreground");
  return (
    <DePresenceProvider value={rings}>
      <DePresenceCardProvider value={presenceCard}>
        <ChartFrame data-screen="chart" className="h-full">
          {header}
          {/* The lens bar (CHART-LENSES): the tabs, then (wave 9) one short line under them with
          the lens's legend — its period, steps and links — that never pushes the bar wider. */}
          <div
            data-slot="chart-lens-bar"
            data-lens={lens}
            className="flex min-w-0 flex-col gap-0.5 border-b border-subtle bg-card px-6 pt-1 pb-1 max-md:px-4"
          >
            <LensTabs
              aria-label="Lens"
              value={lens}
              onValueChange={(v) => pickLens(v as Lens)}
              tabs={lensesServed.map((l) => {
                const level = lensAudience(l, visibility);
                const isLocked = locked(l);
                return {
                  value: l,
                  label: LENS_LABELS[l],
                  ...(isLocked
                    ? {
                        locked: true,
                        note: `Shown to: ${level ? LEVEL_LABELS[level].toLowerCase() : "—"}`,
                      }
                    : {}),
                };
              })}
            />
            <span
              data-slot="chart-lens-legend"
              className="flex h-5 min-w-0 items-center gap-x-4 overflow-hidden text-xs whitespace-nowrap text-muted-foreground"
            >
              <span className="min-w-0 truncate" title={LENS_HINTS[lens]}>
                {lens === "performance" || lens === "execution"
                  ? `${weekRangeLabel(week)}: ${lens === "performance" ? "each seat's scorecard band" : "goal score, weighted target attainment"}`
                  : lens === "finance" && dimsQ.data
                    ? `${monthLabel(dimsQ.data.month)}: spend against budget`
                    : lens === "keystones" && dimsQ.data
                      ? `${dimsQ.data.completeness.quarter}: priorities on track`
                      : lens === "completeness" && dimsQ.data
                        ? `${completenessSummary?.complete ?? 0} of ${plural(completenessSummary?.seats ?? 0, "seat")} fully set up · mean ${completenessSummary?.mean ?? 0}%`
                        : LENS_HINTS[lens]}
              </span>
              {steps.length ? <LensSteps steps={steps} className="shrink-0" /> : null}
              {lens === "finance" ? (
                <Link
                  to="/c/$slug/kpis/reports/finance"
                  params={{ slug: company.slug }}
                  search={keepFocus}
                  className="shrink-0 text-primary-text underline-offset-4 hover:underline"
                >
                  Budget vs actual
                </Link>
              ) : null}
              {lens === "completeness" && manager ? (
                <Link
                  to="/c/$slug/setup/chart"
                  params={{ slug: company.slug }}
                  search={keepFocus}
                  className="shrink-0 text-primary-text underline-offset-4 hover:underline"
                >
                  Required items…
                </Link>
              ) : null}
              {lens === "overall" && dimsQ.data ? (
                <span className="min-w-0 truncate text-faint" data-slot="lens-weights">
                  Weights:{" "}
                  {SCORED_LENSES.filter((p) => lensesServed.includes(p))
                    .map((p) => `${LENS_LABELS[p]} ×${dimsQ.data.lenses.weights[p]}`)
                    .join(" · ")}
                </span>
              ) : null}
            </span>
          </div>
          {/* Phones wrap the row (Chrome sizes the mobile layout viewport to a scroll container's
          unshrinkable items, so a sideways-scrolling row would widen the page). */}
          <Toolbar
            bleed
            aria-label="Org chart tools"
            data-slot="chart-toolbar"
            className="max-md:h-auto max-md:flex-wrap max-md:gap-y-1.5 max-md:py-1.5"
          >
            {!mobile ? (
              <Segmented
                aria-label="Layout"
                size="sm"
                value={view}
                onValueChange={pickView}
                options={[
                  { value: "tree", label: "Chart", icon: NetworkIcon },
                  { value: "table", label: "Table", icon: Table2Icon },
                ]}
              />
            ) : null}
            {/* The period: ‹ › step the focused week by the grain; the label says what is read.
            KPIS-MODE (founder, 2026-10-22): only on the lenses that read a period. */}
            {PERIOD_LENSES.has(lens) ? (
              <span
                data-slot="chart-period"
                className="flex shrink-0 items-center gap-0.5 rounded-md border border-border bg-card p-0.5"
              >
                <Act
                  tip={`Previous ${grain}`}
                  render={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`Previous ${grain}`}
                      onClick={() => stepPeriod(-1)}
                    />
                  }
                >
                  <ChevronLeftIcon aria-hidden="true" />
                </Act>
                <span
                  className="min-w-0 truncate px-1 text-xs text-foreground tabular-nums"
                  data-slot="chart-period-label"
                  title="The week in focus; Finance reads its month, Priorities its quarter"
                >
                  {periodLabel}
                </span>
                <Act
                  tip={`Next ${grain}`}
                  render={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`Next ${grain}`}
                      onClick={() => stepPeriod(1)}
                    />
                  }
                >
                  <ChevronRightIcon aria-hidden="true" />
                </Act>
                <span className="mx-0.5 h-4 w-px bg-border" aria-hidden="true" />
                <Segmented
                  aria-label="Period"
                  size="sm"
                  value={grain}
                  onValueChange={(g) => setGrainPref(g as Grain)}
                  options={(["week", "month", "quarter"] as const).map((g) => ({
                    value: g,
                    label: GRAIN_LABELS[g],
                  }))}
                  className="border-0 bg-transparent p-0 shadow-none"
                />
              </span>
            ) : null}
            <SearchField
              aria-label="Find a seat or person"
              placeholder="Find a seat or person…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-44 shrink-0 max-sm:w-[160px]"
            />
            {/* Teams: a floating panel of radio buttons; the chosen team is enclosed on the canvas. */}
            {view === "tree" && chart.teams.length ? (
              <Act
                tip="Draw a team's enclosure"
                render={
                  <Button
                    variant="outline"
                    size="sm"
                    aria-pressed={teamsOpen}
                    aria-controls="chart-teams-panel"
                    data-slot="chart-teams"
                    className="aria-pressed:border-primary aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
                    onClick={() => setTeamsOpen((v) => !v)}
                  />
                }
              >
                <UsersIcon data-icon="inline-start" aria-hidden="true" />
                Teams
                {/* Wave 9: the enclosure's chip names the team; the button only marks that one is
                drawn, so the toolbar keeps its width at 1440. */}
                {enclosed ? (
                  <span className="inline-flex items-center" data-slot="chart-teams-on">
                    <span aria-hidden="true" className="size-1.5 rounded-full bg-primary" />
                    <span className="sr-only">
                      {chart.teams.find((t) => t.id === enclosed)?.name ?? ""}
                    </span>
                  </span>
                ) : null}
              </Act>
            ) : null}
            {view === "tree" ? (
              <Act
                tip="Fit the whole chart"
                render={
                  <Button
                    variant="outline"
                    size="sm"
                    aria-pressed={fit}
                    data-slot="chart-fit"
                    className="aria-pressed:border-primary aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
                    onClick={() => setFit(true)}
                  />
                }
              >
                <MaximizeIcon data-icon="inline-start" aria-hidden="true" />
                Fit
              </Act>
            ) : null}
            <span className="ml-auto flex shrink-0 items-center gap-0.5">
              {scopeId ? (
                <FilterChip
                  data-slot="chart-scope"
                  label={
                    scopeTeamRow
                      ? `Team: ${scopeTeamRow.name} · ${plural(seats.length, "seat")}`
                      : "Team not in this chart"
                  }
                  removeLabel="Whole company"
                  onRemove={() => setFocus({ scope: null })}
                  className="mr-1"
                />
              ) : null}
              <Act
                tip={line ? "Show the whole chart" : "My line: my seats, above and below"}
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-pressed={line}
                    aria-label="My line"
                    data-slot="chart-my-line"
                    className="text-muted-foreground aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
                    onClick={() => setLine((v) => !v)}
                  />
                }
              >
                <RouteIcon aria-hidden="true" />
              </Act>
              {/* Card details: what a Structure card carries beyond the floor (CHART-DIMENSIONS's
              Dimensions menu, now secondary to the lenses). */}
              <DropdownMenu>
                <Act
                  tip={`Card details${lens === "structure" ? "" : " (the Structure lens)"}: ${preset ? PRESET_LABELS[preset] : `${dims.size} of ${served.length}`}`}
                  render={
                    <DropdownMenuTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Card details"
                          data-slot="chart-dimensions"
                          className="text-muted-foreground"
                        />
                      }
                    />
                  }
                >
                  <LayersIcon aria-hidden="true" />
                  <span className="sr-only" data-slot="chart-dimensions-state">
                    {preset ? PRESET_LABELS[preset] : `${dims.size} of ${served.length}`}
                  </span>
                </Act>
                <DropdownMenuContent align="end" className="min-w-64">
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>
                      Preset{lens !== "structure" ? " (the Structure lens)" : ""} ·{" "}
                      {preset ? PRESET_LABELS[preset] : `${dims.size} of ${served.length}`}
                    </DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                      value={preset ?? ""}
                      onValueChange={(v) =>
                        chooseDims(
                          new Set(
                            PRESET_DIMENSIONS[v as (typeof PRESETS)[number]].filter((d) =>
                              served.includes(d),
                            ),
                          ),
                        )
                      }
                    >
                      {PRESETS.map((pr) => (
                        <DropdownMenuRadioItem key={pr} value={pr} data-preset={pr}>
                          {PRESET_LABELS[pr]}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>On every card</DropdownMenuLabel>
                    {served.map((d) => (
                      <DropdownMenuCheckboxItem
                        key={d}
                        checked={dims.has(d)}
                        onCheckedChange={(on) => toggleDim(d, on)}
                        data-dimension={d}
                      >
                        <span className="flex min-w-0 flex-col">
                          <span>{DIMENSION_LABELS[d]}</span>
                          <span className="text-xs text-faint">
                            {GATED.includes(d as (typeof GATED)[number])
                              ? LEVEL_LABELS[visibility[d as (typeof GATED)[number]]]
                              : DIMENSION_HINTS[d]}
                          </span>
                        </span>
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    {prefs !== null ? (
                      <DropdownMenuItem onClick={() => chooseDims(null)}>
                        Use the company default
                      </DropdownMenuItem>
                    ) : null}
                    {manager ? (
                      <DropdownMenuItem
                        disabled={saveDefault.isPending}
                        onClick={() => saveDefault.mutate()}
                      >
                        Save as the company default
                      </DropdownMenuItem>
                    ) : null}
                    {manager ? (
                      <DropdownMenuItem
                        render={
                          <Link
                            to="/c/$slug/setup/chart"
                            params={{ slug: company.slug }}
                            search={keepFocus}
                          />
                        }
                      >
                        Who sees what…
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Wave 9 (KPI Beast's "View"): the secondary tools in one menu so the toolbar fits
              1440 — the lines shown, what a drop sets, expand / collapse all. */}
              <DropdownMenu>
                <Act
                  tip={`View: lines shown ${visibleLines.size + 1} of ${LINE_KINDS.length + 1}${manager ? `; drag sets ${EDGE_LABELS[dragKind].toLowerCase()}` : ""}`}
                  render={
                    <DropdownMenuTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="sm"
                          data-slot="chart-view-menu"
                          className="gap-1 px-2 text-muted-foreground"
                        />
                      }
                    />
                  }
                >
                  <SlidersHorizontalIcon data-icon="inline-start" aria-hidden="true" />
                  View
                </Act>
                <DropdownMenuContent align="end" className="min-w-56">
                  <DropdownMenuGroup data-slot="chart-lines">
                    <DropdownMenuLabel>Lines shown</DropdownMenuLabel>
                    <DropdownMenuCheckboxItem checked disabled>
                      {EDGE_LABELS.reports_to}
                      <span className="ml-auto pl-3 text-xs text-faint">the tree</span>
                    </DropdownMenuCheckboxItem>
                    {LINE_KINDS.map((k) => (
                      <DropdownMenuCheckboxItem
                        key={k}
                        checked={visibleLines.has(k)}
                        onCheckedChange={(on) =>
                          setVisibleLines((prev) => {
                            const next = new Set(prev);
                            if (on) next.add(k);
                            else next.delete(k);
                            return next;
                          })
                        }
                      >
                        {EDGE_LABELS[k]}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuGroup>
                  {manager ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup data-slot="chart-drag">
                        <DropdownMenuLabel>Dropping a seat on another sets</DropdownMenuLabel>
                        <DropdownMenuRadioGroup
                          value={dragKind}
                          onValueChange={(v) => setDragKind(v as EdgeKind)}
                        >
                          {EDGE_KINDS.map((k) => (
                            <DropdownMenuRadioItem key={k} value={k}>
                              {EDGE_LABELS[k]}
                            </DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuGroup>
                    </>
                  ) : null}
                  {withChildren.size ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        <DropdownMenuItem
                          disabled={collapsed.size === 0}
                          onClick={() => setCollapsed(new Set())}
                        >
                          <ChevronsUpDownIcon aria-hidden="true" />
                          Expand all
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={collapsed.size >= withChildren.size}
                          onClick={() => setCollapsed(new Set(withChildren))}
                        >
                          <ChevronsDownUpIcon aria-hidden="true" />
                          Collapse all
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
              <Popover>
                <PopoverTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Legend"
                      data-slot="chart-legend"
                      className={quiet}
                    />
                  }
                >
                  <InfoIcon aria-hidden="true" />
                </PopoverTrigger>
                <PopoverContent align="end" className="w-72 p-3">
                  <div className="flex flex-col gap-3" data-slot="legend">
                    {/* Wave 9: the lens's full explanation lives here; the bar keeps one short line. */}
                    <div className="flex flex-col gap-1" data-slot="legend-lens" data-lens={lens}>
                      <span className="text-[11px] leading-4 font-medium tracking-wide text-faint uppercase">
                        This lens · {LENS_LABELS[lens]}
                      </span>
                      <p className="text-xs leading-5 text-muted-foreground">{LENS_HINTS[lens]}.</p>
                    </div>
                    <LegendSection title="Seats">
                      <LegendItem
                        swatch={<Swatch shape="dashed" />}
                        label="Open seat"
                        note="nobody on it"
                      />
                      <LegendItem
                        swatch={<Swatch className="bg-primary-soft" />}
                        label="Digital / Mixed"
                        note="a DE holds it"
                      />
                      <LegendItem swatch={<Swatch className="bg-primary" />} label="In focus" />
                      <LegendItem
                        swatch={<Swatch className="bg-muted opacity-50" />}
                        label="Dimmed"
                        note="nothing in this lens"
                      />
                    </LegendSection>
                    <LegendSection title="Lines">
                      <LegendItem
                        swatch={<Swatch shape="line" className="bg-strong" />}
                        label="Reports to"
                        note="the tree"
                      />
                      <LegendItem
                        swatch={<Swatch className="bg-warning-soft" />}
                        label="Authority"
                        note="approves for"
                      />
                      <LegendItem
                        swatch={<Swatch className="bg-info-soft" />}
                        label="Delegation"
                        note="hands work to"
                      />
                    </LegendSection>
                    {served.includes("kpi") ? (
                      <LegendSection title={`KPI status · week ${week}`}>
                        {BANDS.map((b) => (
                          <LegendItem
                            key={b}
                            swatch={<Swatch className={`bg-band-${b.replace("_", "-")}`} />}
                            label={BAND_LABELS[b]}
                          />
                        ))}
                      </LegendSection>
                    ) : null}
                    <LegendSection title="Load and cost">
                      <LegendItem swatch={<Swatch className="bg-warning" />} label="Overdue work" />
                      <LegendItem
                        swatch={<Swatch className="bg-danger" />}
                        label="Half or more overdue · over budget"
                      />
                    </LegendSection>
                    <LegendSection title="Lenses">
                      {lensesServed.map((l) => (
                        <LegendItem
                          key={l}
                          swatch={<Swatch data-lens={l} className="bg-(--lens)" />}
                          label={LENS_LABELS[l]}
                          note={l === "structure" || l === "responsibilities" ? "floor" : undefined}
                          title={LENS_HINTS[l]}
                        />
                      ))}
                    </LegendSection>
                    <LegendSection title="Who sees what">
                      {GATED.map((g) => (
                        <LegendItem
                          key={g}
                          swatch={<Swatch className="bg-muted" />}
                          label={GATED_LABELS[g]}
                          note={LEVEL_LABELS[visibility[g]]}
                        />
                      ))}
                      <LegendItem
                        swatch={<Swatch className="bg-muted" />}
                        label="Seat setup"
                        note={LEVEL_LABELS.reporting_line}
                      />
                    </LegendSection>
                  </div>
                </PopoverContent>
              </Popover>
            </span>
          </Toolbar>
          {lens === "structure" && (dims.has("kpi") || dims.has("cost")) ? (
            <p
              className="border-b border-subtle px-6 py-1.5 text-xs text-muted-foreground max-md:px-4"
              data-slot="overlay-legend"
            >
              {dims.has("kpi")
                ? `KPI status of week ${week} (${weekRangeLabel(week)}) from each seat's scorecard; "Not scored" is a seat without metrics. `
                : ""}
              {dims.has("cost") && dimsQ.data
                ? `Cost: ${monthLabel(dimsQ.data.month)}, each seat's spend against its budget, pay included where you may read it. `
                : ""}
              {dims.has("cost") ? (
                <Link
                  to="/c/$slug/kpis/reports/finance"
                  params={{ slug: company.slug }}
                  search={keepFocus}
                  className="text-primary-text underline-offset-4 hover:underline"
                >
                  Budget vs actual by team
                </Link>
              ) : null}
            </p>
          ) : null}
          {line && lineSeats && lineSeats.size === 0 ? (
            <p
              role="status"
              className="border-b border-subtle px-6 py-1.5 text-xs text-muted-foreground max-md:px-4"
              data-slot="my-line-empty"
            >
              You hold no seat in this chart, so "My line" has nothing to keep.
            </p>
          ) : null}
          {ops.length > 0 ? (
            <div
              role="status"
              data-slot="chart-draft"
              className="sticky top-0 z-sticky flex min-h-9 flex-wrap items-center gap-x-3 gap-y-1 border-b border-warning-soft bg-warning-soft px-6 py-1 text-sm text-warning-text max-md:px-4"
            >
              <span className="font-medium">{plural(ops.length, "unsaved change")}</span>
              <span className="text-xs">
                Nothing is saved until you review. Dashed seats have moved.
              </span>
              <span className="ml-auto flex items-center gap-1.5">
                <Button variant="ghost" size="sm" onClick={() => setOps([])}>
                  Discard
                </Button>
                <Button size="sm" onClick={() => setReviewing(true)}>
                  Review and apply
                </Button>
              </span>
            </div>
          ) : null}
          {notice ? (
            <p
              role="status"
              className="border-b border-subtle px-6 py-1.5 text-xs text-warning-text max-md:px-4"
              data-slot="drop-refused"
            >
              {notice}
            </p>
          ) : null}
          {dimsQ.isError && needsDimensions(dims) ? (
            <ChartBody className="flex-none">
              <LoadError what="The seat dimensions" error={dimsQ.error} />
            </ChartBody>
          ) : null}
          {!drawn.length ? (
            <ChartBody>
              <EmptyState
                variant="card"
                icon={NetworkIcon}
                title={
                  found
                    ? "No seat or person matches"
                    : inScope
                      ? "No seats in this team"
                      : "No seats yet"
                }
                description={
                  found
                    ? "Try another name or title."
                    : inScope
                      ? "Add a seat to the team."
                      : "Create seats, then put people and digital employees on them."
                }
                actions={
                  manager && !found ? (
                    inScope && scopeTeamRow ? (
                      // MODALS: the seat joins the team here, not in Setup › Teams.
                      <Button
                        onClick={() => setJoining({ id: scopeTeamRow.id, name: scopeTeamRow.name })}
                      >
                        <PlusIcon data-icon="inline-start" />
                        Add a seat
                      </Button>
                    ) : (
                      <Button onClick={() => setAdding(null)} disabled={!roles.data}>
                        <PlusIcon data-icon="inline-start" />
                        New seat
                      </Button>
                    )
                  ) : null
                }
              />
            </ChartBody>
          ) : (
            <div data-slot="chart-stage" className="flex min-h-0 min-w-0 flex-1 flex-row">
              <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
                {scopeTeamRow && kpiFeature.enabled ? (
                  <TeamStrip
                    api={api}
                    company={company}
                    team={{ id: scopeTeamRow.id, name: scopeTeamRow.name }}
                    week={week}
                  />
                ) : null}
                <div
                  ref={canvasRef}
                  data-slot="chart-canvas"
                  data-dotted={view === "tree" ? "" : undefined}
                  data-panning={panning ? "" : undefined}
                  className={cn(
                    "flex min-h-0 min-w-0 flex-1 flex-col",
                    view === "tree" ? "cursor-grab overflow-auto bg-sunken" : "overflow-auto",
                    panning && "cursor-grabbing select-none",
                  )}
                  onPointerDown={onCanvasPointerDown}
                  onPointerMove={onCanvasPointerMove}
                  onPointerUp={onCanvasPointerUp}
                  onPointerCancel={onCanvasPointerUp}
                >
                  {view === "tree" ? (
                    <div ref={stageRef} className="relative min-w-max">
                      {enclosure && enclosed ? (
                        <TeamEnclosure
                          data-team={enclosed}
                          rect={enclosure}
                          label={chart.teams.find((t) => t.id === enclosed)?.name ?? "Team"}
                          chip={
                            teamScoreOf.get(enclosed) ? (
                              <TeamScoreChip score={teamScoreOf.get(enclosed) as TeamScore} />
                            ) : undefined
                          }
                        />
                      ) : null}
                      <OrgTree
                        ref={treeRef}
                        view="tree"
                        zoom={zoom}
                        compact={compact}
                        data-slot="seat-tree"
                        aria-label="Seats"
                        className="cursor-default"
                      >
                        {tree.map((n) => seatCard(n))}
                      </OrgTree>
                    </div>
                  ) : (
                    tableView
                  )}
                  {manager && dragging && dragKind === "reports_to" ? (
                    <div
                      {...dropTarget(null)}
                      data-drop={over === "top" ? "over" : undefined}
                      data-slot="drop-top"
                      className="mx-6 mb-6 rounded-md border border-dashed border-strong p-3 text-center text-xs text-muted-foreground data-[drop=over]:border-primary data-[drop=over]:bg-primary-soft max-md:mx-4"
                    >
                      Drop here: the seat reports to nobody (top of the chart)
                    </div>
                  ) : null}
                </div>
                {/* CHART-KB: the Teams floating panel — one radio per team, "None" first. */}
                {view === "tree" && teamsOpen ? (
                  <fieldset
                    id="chart-teams-panel"
                    data-slot="chart-teams-panel"
                    aria-label="Teams"
                    className="absolute top-3 right-3 z-10 flex max-h-[60%] w-52 flex-col gap-0.5 overflow-y-auto rounded-md border border-border bg-card p-2 shadow-md"
                  >
                    <span className="flex items-center justify-between gap-2 px-1 pb-1">
                      <span className="text-[11px] leading-4 font-medium tracking-wide text-faint uppercase">
                        Teams
                      </span>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Close the Teams panel"
                        onClick={() => setTeamsOpen(false)}
                      >
                        <XIcon aria-hidden="true" />
                      </Button>
                    </span>
                    {[{ id: "", name: "None" }, ...chart.teams].map((t) => (
                      <label
                        key={t.id || "none"}
                        className="flex cursor-pointer items-center gap-2 rounded-sm px-1.5 py-1 text-xs hover:bg-sunken"
                      >
                        <input
                          type="radio"
                          name="chart-team-enclosure"
                          value={t.id}
                          checked={(enclosed ?? "") === t.id}
                          onChange={() => setEnclosed(t.id || null)}
                          className="accent-primary"
                        />
                        <span className="min-w-0 flex-1 truncate">{t.name}</span>
                        {t.id && teamScoreOf.get(t.id) ? (
                          <TeamScoreChip score={teamScoreOf.get(t.id) as TeamScore} />
                        ) : null}
                        {t.id ? (
                          <span className="shrink-0 text-faint tabular-nums">
                            {teamSeats(chart, t.id).size}
                          </span>
                        ) : null}
                      </label>
                    ))}
                  </fieldset>
                ) : null}
                {view === "tree" ? (
                  <fieldset
                    aria-label="Zoom"
                    data-slot="chart-zoom"
                    className="absolute right-3 bottom-3 z-10 flex items-center gap-0.5 rounded-md border border-border bg-card p-0.5 shadow-sm"
                  >
                    <Act
                      tip="Zoom out"
                      render={
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label="Zoom out"
                          disabled={zoom <= ZOOM_MIN}
                          onClick={() => zoomTo(zoom / ZOOM_STEP)}
                        />
                      }
                    >
                      <MinusIcon aria-hidden="true" />
                    </Act>
                    <span
                      className="min-w-10 text-center text-xs text-muted-foreground tabular-nums"
                      aria-live="polite"
                      data-slot="chart-zoom-level"
                    >
                      {Math.round(zoom * 100)}%
                    </span>
                    <Act
                      tip="Zoom in"
                      render={
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label="Zoom in"
                          disabled={zoom >= ZOOM_MAX}
                          onClick={() => zoomTo(zoom * ZOOM_STEP)}
                        />
                      }
                    >
                      <PlusIcon aria-hidden="true" />
                    </Act>
                    <span className="mx-0.5 h-4 w-px bg-border" aria-hidden="true" />
                    <Act
                      tip="Fit the whole tree"
                      render={
                        <Button
                          variant="ghost"
                          size="xs"
                          aria-pressed={fit}
                          className="aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
                          onClick={() => setFit(true)}
                        />
                      }
                    >
                      Fit
                    </Act>
                    <Act
                      tip="Actual size"
                      render={
                        <Button
                          variant="ghost"
                          size="xs"
                          aria-pressed={!fit && zoom === 1}
                          className="tabular-nums aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
                          onClick={() => zoomTo(1)}
                        />
                      }
                    >
                      100%
                    </Act>
                  </fieldset>
                ) : null}
              </div>
              {panel ? (
                <SeatPanel
                  // A seat's own drawer: what another seat had open (a modal, a task) closes with it.
                  key={panel.id}
                  api={api}
                  company={company}
                  seat={panel}
                  chart={chart}
                  sup={sup}
                  roleName={roleName.get(panel.role_id) ?? "Role"}
                  teams={teamOf.get(panel.id) ?? []}
                  lines={chart ? linesOf(chart, ops) : []}
                  titleOf={titleOf}
                  dims={dimOf.get(panel.id)}
                  pending={dimsQ.isPending}
                  served={served}
                  visibility={visibility}
                  week={week}
                  month={dimsQ.data?.month ?? null}
                  quarter={dimsQ.data?.completeness.quarter ?? null}
                  required={dimsQ.data?.completeness.required}
                  lens={lens}
                  lenses={lensesServed}
                  thresholds={thresholds}
                  onClose={() => setPanelSeat(null)}
                  onOpenSeat={(id) => setPanelSeat(id)}
                  onFocus={() =>
                    setFocus({
                      subject: focusedSeat === panel.id ? null : { kind: "seat", id: panel.id },
                    })
                  }
                  onLines={manager ? () => setLinesFor(panel.id) : undefined}
                  onReportsTo={
                    manager && panel.status !== "closed" ? () => setMoving(panel.id) : undefined
                  }
                  focused={focusedSeat === panel.id}
                />
              ) : null}
            </div>
          )}
          {lineSeat ? (
            <SeatLinesDialog
              chart={chart}
              ops={ops}
              seat={lineSeat}
              onOp={apply}
              onClose={() => setLinesFor(null)}
            />
          ) : null}
          {moving && chart ? (
            <ReportsToDialog
              chart={chart}
              ops={ops}
              seatId={moving}
              onClose={() => setMoving(null)}
              onPick={(to) => {
                stageMove(moving, to);
                setMoving(null);
              }}
            />
          ) : null}
          {editing ? (
            <EditSeatDrawer
              api={api}
              company={company}
              seatId={editing}
              {...(roles.data ? { roles: roles.data.pages.flatMap((p) => p.data) } : {})}
              onClose={() => setEditing(null)}
            />
          ) : null}
          {joining ? (
            <TeamMemberDialog
              api={api}
              co={company.id}
              of={{ kind: "team", ...joining }}
              onClose={() => setJoining(null)}
            />
          ) : null}
          {reviewing ? (
            <ReviewDialog
              api={api}
              companyId={company.id}
              chart={chart}
              ops={ops}
              onClose={() => setReviewing(false)}
              onApplied={() => {
                setReviewing(false);
                setOps([]);
                setNotice("The chart is saved.");
              }}
            />
          ) : null}
          {adding !== false && roles.data ? (
            <NewSeatDialog
              api={api}
              co={company.id}
              roles={roles.data.pages.flatMap((p) => p.data)}
              seats={chart.seats.filter((s) => s.status !== "closed")}
              supervisorId={adding}
              onClose={() => setAdding(false)}
              onCreated={(id) => {
                setAdding(false);
                void queryClient.invalidateQueries({ queryKey: chartKeys.orgChart(company.id) });
                setFocus({ subject: { kind: "seat", id } });
              }}
            />
          ) : null}
        </ChartFrame>
      </DePresenceCardProvider>
    </DePresenceProvider>
  );
}

/**
 * TEAM-KPI (founder-approved 2026-10-21): the chart scoped to a team (the sidebar's team view)
 * shows the team's scorecard above the canvas, collapsible (open by default).
 */
function TeamStrip({
  api,
  company,
  team,
  week,
}: {
  api: Api;
  company: CompanyAccess;
  team: { id: string; name: string };
  week: string;
}) {
  const [open, setOpen] = useState(true);
  return (
    <section
      data-slot="chart-team-scorecard"
      aria-label={`${team.name}: team KPIs`}
      className="border-b border-border bg-card px-4 py-2"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronRightIcon
          aria-hidden="true"
          className={cn("size-3.5 transition-transform", open && "rotate-90")}
        />
        {team.name} · team KPIs
      </button>
      {open ? (
        <div className="max-h-64 overflow-y-auto pt-2">
          <TeamScorecardCard api={api} company={company} team={team} week={week} compact />
        </div>
      ) : null}
    </section>
  );
}
